/**
 * Scrapers/Onboard — feasibility tool for adding a new scraping
 * target.
 *
 * Stage 1 of the autonomous-onboarding pipeline (B1 of 5). The
 * remaining stages — auto-profile (B2), generate scraper code (B3),
 * open PR (B4), wire-end-to-end (B5) — show as roadmap cards below
 * the matrix until they ship.
 *
 * What this tab does:
 *   1. User pastes a URL (and optionally search keyword + selector)
 *   2. We POST to /probe-engines/compare with all 5 engines
 *   3. We score each result on a 0-100 feasibility scale
 *   4. We recommend the cheapest engine that works (preference
 *      order: direct_http > cf_http > docker_http > cf_browser
 *      > docker_browser, all else equal)
 *   5. User can drill into any individual run via the Diagnose
 *      history detail page (compare runs are persisted there too)
 *
 * Scoring policy is intentionally simple and printed on-screen so
 * an operator sees exactly why an engine got the score it did. The
 * scoring weights live in scoreEngine() — change them there and
 * the matrix updates immediately.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, ArrowRight, Beaker, CalendarClock, CheckCircle2, ChevronRight,
  Cookie, Download, ExternalLink, FileText, GitMerge, GitPullRequest,
  GitPullRequestClosed, Loader2, Package, Play, RefreshCw, Save, Sparkles, Wand2,
} from 'lucide-react';
import {
  fetchArtefact,
  runCompare,
  type EngineKind,
  type ProbeResult,
} from '@/api/probe-engines';
import {
  generateAndDownloadScraper,
  openOnboardingPR,
  createOnboardRun,
  patchOnboardRun,
  listOnboardRuns,
  listPendingRegisterRuns,
  getOnboardingPRState,
  registerOnboardedScraper,
  mergeOnboardPr,
  closeOnboardPr,
  rerunOnboardPrCi,
  PrNotMergedError,
  type FeasibilityRow as PRFeasibilityRow,
  type ProfileSummary as PRProfileSummary,
  type OnboardRun,
  type OnboardRunSummary,
  type OnboardStage,
  type OpenPRResult,
  type GenerateRequest,
} from '@/api/onboard';
import {
  buildProfile,
  type SiteProfile,
} from '@/lib/onboard-heuristics';
import { cn } from '@/lib/cn';
import {
  DEFAULT_SEARCH_TERMS_CSV,
  DEFAULT_SEARCH_TERMS_COUNT,
} from '@/lib/onboard-default-search-terms';

const ENGINES: EngineKind[] = ['direct_http', 'docker_http', 'docker_browser', 'cf_http', 'cf_browser'];
// Ranked cheapest-first so the recommendation breaks ties toward
// the lowest-cost engine when scores are equal.
const COST_RANK: EngineKind[] = ['direct_http', 'cf_http', 'docker_http', 'cf_browser', 'docker_browser'];

/** Page-type model.
 *  - 'auto' (default) — detect from the Recon response Content-Type.
 *    JSON content-type → 'json' branch, anything else → 'html'.
 *  - 'html' / 'json' — explicit operator override that suppresses
 *    auto-detection. Useful when a site returns text/html for a JSON
 *    payload (or vice-versa) and the heuristic guesses wrong. */
export type PageType = 'auto' | 'html' | 'json';

interface FormState {
  url:             string;
  pageType:        PageType;
  /** JMESPath-ish dot-path to the products array in the JSON
   *  response (e.g. `response.data` for the CeX search API,
   *  `results` for many REST APIs). Only consulted when the
   *  effective page type resolves to 'json'. Empty = let the
   *  profile heuristic guess. */
  productsPath:    string;
  waitForSelector: string;
  searchSelector:  string;
  searchKeyword:   string;
  screenshot:      boolean;
}

const INITIAL: FormState = {
  url:             '',
  pageType:        'auto',
  productsPath:    '',
  waitForSelector: '',
  searchSelector:  '',
  searchKeyword:   '',
  screenshot:      true,
};

/** Resolve effective page type from a form override + Recon results.
 *  Caller passes the override; we look at the first successful
 *  result's content-type header when override is 'auto'. Falls back
 *  to 'html' when no header is available (matches legacy behavior). */
/** Carries the active onboard_run id down to deeply-nested children
 *  (Stage 5 in particular) without prop-drilling through Profile →
 *  Generate → Stage5. null = no run created yet. */
const RunIdContext = createContext<string | null>(null);

export function resolvePageType(override: PageType, results: ProbeResult[] | null): 'html' | 'json' {
  if (override !== 'auto') return override;
  if (!results) return 'html';
  for (const r of results) {
    if (r.error) continue;
    const ct = (r.responseHeaders?.['content-type'] ?? r.responseHeaders?.['Content-Type'] ?? '').toLowerCase();
    if (!ct) continue;
    if (ct.includes('application/json') || ct.includes('text/json') || ct.includes('+json')) return 'json';
    return 'html';
  }
  return 'html';
}

export default function OnboardTab() {
  const [form,    setForm]    = useState<FormState>(INITIAL);
  const [results, setResults] = useState<ProbeResult[] | null>(null);
  // Server-side run id, set after the createOnboardRun() call lands.
  // Stages downstream of Recon PATCH this id to record their progress
  // (B5). Persistence is best-effort — we never block UX on it.
  const [runId,   setRunId]   = useState<string | null>(null);
  // Bumping this counter remounts the dependent stages so their
  // internal state (Profile, GenerateForm, JsonGenerateForm, openPRM
  // result) resets — operator can start a new retailer immediately
  // after Stage 4 lands a PR.
  const [pipelineKey, setPipelineKey] = useState(0);
  const queryClient = useQueryClient();

  /** Reset Stages 1–4 so the operator can onboard the next retailer
   *  without leftover state. Called automatically after Stage 4
   *  succeeds (≤4s delay so the operator can read the PR link)
   *  and also exposed as a manual "Start new retailer" button. The
   *  Stage-5 history panel survives — that's the whole point of
   *  having it, the operator can register the just-merged PR there
   *  whenever it lands. */
  function resetPipeline() {
    setForm(INITIAL);
    setResults(null);
    setRunId(null);
    setPipelineKey((k) => k + 1);
  }

  /** Best-effort PATCH — fires-and-forgets, refreshes the history list
   *  on success. Each downstream stage calls this with the relevant
   *  payload so the history panel reflects mid-pipeline progress. */
  async function recordStage(patch: Parameters<typeof patchOnboardRun>[1]): Promise<void> {
    if (!runId) return;
    try {
      await patchOnboardRun(runId, patch);
      // Bust the list-runs cache so the panel reflects the latest stage.
      queryClient.invalidateQueries({ queryKey: ['onboardRuns'] });
    } catch {
      // Persistence failures must not break the pipeline UX. The error
      // surfaces in the network tab; user can still finish the run.
    }
  }

  const runM = useMutation({
    mutationFn: async () => {
      if (!form.url) throw new Error('URL is required');
      // Browser opts only matter for browser engines (docker_browser /
      // cf_browser). Forward them anyway — HTTP engines ignore the
      // browser block at the runner side.
      return runCompare({
        url:     form.url,
        engines: ENGINES,
        browser: {
          screenshot:       form.screenshot,
          useHumanBehavior: true,
          waitForSelector:  form.waitForSelector || undefined,
          searchSelector:   form.searchSelector  || undefined,
          searchKeyword:    form.searchKeyword   || undefined,
          humanevasionTier: 'cdp',
        },
        timeoutMs: 45_000,
      });
    },
    onSuccess: async (data) => {
      setResults(data.results);
      // Best-effort persistence — create a server-side run record so
      // downstream stages can PATCH it. Failures are silent; the
      // pipeline still works without the history row.
      try {
        const recommended = pickRecommendation(data.results).winner;
        const matrix      = buildMatrixForPersist(data.results);
        const run = await createOnboardRun({
          targetUrl: form.url,
          recon: {
            recommendedEngine: recommended?.engine ?? '',
            matrix,
          },
        });
        setRunId(run.id);
        queryClient.invalidateQueries({ queryKey: ['onboardRuns'] });
      } catch {
        setRunId(null);
      }
    },
  });

  return (
    <RunIdContext.Provider value={runId}>
    <div className="space-y-6 max-w-5xl">
      {/* Stage-5 history panel sits at the top so a fire-and-forget
          operator can register merged-and-ready scrapers from prior
          runs without scrolling to find them. Solves the "switching
          retailers wipes my Stage 5" complaint by surviving the
          active form's lifecycle. */}
      <Stage5HistoryPanel />
      <ReconForm
        form={form}
        setForm={setForm}
        running={runM.isPending}
        onRun={() => runM.mutate()}
      />

      {runM.error && (
        <div className="border border-red-500/30 bg-red-500/10 text-red-200 text-sm rounded p-3">
          {(runM.error as Error).message}
        </div>
      )}

      {results
        ? (
          // Keying the dependent stages on `pipelineKey` makes the
          // Profile + Generate forms remount when the operator finishes
          // a retailer — internal state (the GenerateForm fields, the
          // ProfileCard, the openPRM result) is cleared without needing
          // explicit reset plumbing in each child component.
          <div key={pipelineKey} className="space-y-6">
            <FeasibilityMatrix results={results} />
            <Recommendation results={results} />
            <ProfileSection
              url={form.url}
              results={results}
              pageType={resolvePageType(form.pageType, results)}
              pageTypeOverride={form.pageType}
              productsPath={form.productsPath}
              recordStage={recordStage}
              onPipelineComplete={resetPipeline}
            />
          </div>
        )
        : !runM.isPending && (
          <>
            <EmptyHint />
            {/* Stages 2–5 are gated on Stage 1 — each consumes the
                previous stage's output (or, for Stage 5, the merged
                PR from Stage 4). Render locked placeholders so the
                full pipeline structure is visible from the first
                page load, not hidden behind 'run Stage 1 first'. */}
            <LockedStage
              status="live"
              icon={<Beaker className="w-4 h-4 text-cyan-300/40" aria-hidden />}
              title="Stage 2 — Profile"
              hint="Auto-discovers listing-card selector, cookie banner, JS-render need, evasion tier — runs once Stage 1 has captured HTML to analyze."
            />
            <LockedStage
              status="live"
              icon={<Package className="w-4 h-4 text-purple-300/40" aria-hidden />}
              title="Stage 3 — Generate scraper"
              hint="Renders the discovered config into a downloadable scraper directory (Go source + Dockerfile + compose snippet + cleaner stub) — needs a profile from Stage 2."
            />
            <LockedStage
              status="live"
              icon={<GitPullRequest className="w-4 h-4 text-emerald-300/40" aria-hidden />}
              title="Stage 4 — Open PR"
              hint="One-click GitHub PR with the generated scraper, feasibility matrix, and profile evidence baked into the body. Needs a profile from Stage 2 + a generated config from Stage 3."
            />
            <LockedStage
              status="live"
              icon={<CalendarClock className="w-4 h-4 text-amber-300/40" aria-hidden />}
              title="Stage 5 — Register & schedule"
              hint="Once Stage 4 lands a PR and you merge it, this stage writes scrapers_registry and the cron coordinator picks it up on the next 30-min tick."
            />
          </>
        )}

      {/* Run history sits alongside Stage 1, not behind it — it's the
          audit log of every onboarding run (across sessions), so showing
          it from the first page load lets an operator pick up an
          interrupted pipeline without having to re-run Stage 1. The
          panel itself handles its own empty / loading / error states. */}
      <RecentRunsPanel />

      <RoadmapCards />
    </div>
    </RunIdContext.Provider>
  );
}

// ── helpers shared between OnboardTab + GenerateSection ──────────

/** Build the persistable feasibility matrix from raw probe results.
 *  Hoisted out of GenerateSection so OnboardTab can call it on
 *  Recon success (before the user has clicked into Profile). */
function buildMatrixForPersist(results: ProbeResult[]): PRFeasibilityRow[] {
  return results.map((r) => {
    const sc = r.statusCode ?? 0;
    return {
      engine: r.engine,
      ok:     !r.error && sc >= 200 && sc < 400,
      status: r.statusCode ?? undefined,
      ms:     r.timing?.total,
      score:  scoreEngine(r).total,
      note:   scoreEngine(r).reason,
    };
  });
}

// ── Form ───────────────────────────────────────────────────────────

function ReconForm({
  form, setForm, running, onRun,
}: {
  form:    FormState;
  setForm: (f: FormState) => void;
  running: boolean;
  onRun:   () => void;
}) {
  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm({ ...form, [k]: v });
  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
      <h2 className="text-sm font-semibold">Stage 1 — Recon</h2>
      <p className="text-xs text-slate-500">
        Paste a URL and we'll fire it through all five engines in parallel,
        score each on feasibility, and tell you which one to use.
      </p>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-slate-300">URL to test</label>
        <input
          type="url"
          value={form.url}
          placeholder="https://www.argos.co.uk/search/iphone-15"
          onChange={(e) => update('url', e.target.value)}
          className="input w-full"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-300">Page type</label>
        <div className="flex items-center gap-3 text-xs text-slate-300">
          {(['auto', 'html', 'json'] as const).map((pt) => (
            <label key={pt} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="pageType"
                value={pt}
                checked={form.pageType === pt}
                onChange={() => update('pageType', pt)}
                className="accent-emerald-400"
              />
              <span className="font-mono uppercase text-[11px]">{pt}</span>
            </label>
          ))}
          <span className="text-[10px] text-slate-500 ml-1">
            auto = sniff response Content-Type (json payload → JSON branch, otherwise HTML)
          </span>
        </div>
      </div>

      {form.pageType === 'json' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Products list path (JMESPath / dot-path, optional)">
            <input
              value={form.productsPath}
              placeholder="response.data, results, data.products[*]"
              onChange={(e) => update('productsPath', e.target.value)}
              className="input w-full font-mono text-xs"
              spellCheck={false}
            />
          </Field>
          <Field label="Search keyword (used in URL pattern)">
            <input
              value={form.searchKeyword}
              placeholder="iphone 15"
              onChange={(e) => update('searchKeyword', e.target.value)}
              className="input w-full font-mono text-xs"
            />
          </Field>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Field label="Listing-card selector (optional)">
            <input
              value={form.waitForSelector}
              placeholder=".s-item, [data-testid=product-tile]"
              onChange={(e) => update('waitForSelector', e.target.value)}
              className="input w-full font-mono text-xs"
            />
          </Field>
          <Field label="Search input selector (optional)">
            <input
              value={form.searchSelector}
              placeholder="input[name=q]"
              onChange={(e) => update('searchSelector', e.target.value)}
              className="input w-full font-mono text-xs"
            />
          </Field>
          <Field label="Search keyword">
            <input
              value={form.searchKeyword}
              placeholder="iphone 15"
              onChange={(e) => update('searchKeyword', e.target.value)}
              className="input w-full font-mono text-xs"
            />
          </Field>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={form.screenshot}
            onChange={(e) => update('screenshot', e.target.checked)}
            className="accent-emerald-400"
          />
          Capture screenshot from each browser engine
        </label>

        <button
          type="button"
          onClick={onRun}
          disabled={running || !form.url}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded',
            'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30',
            'disabled:opacity-50 disabled:cursor-not-allowed transition',
          )}
        >
          {running
            ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Running 5 engines…</>
            : <><Play className="w-4 h-4" aria-hidden /> Run feasibility check</>}
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-[11px] text-slate-400">{label}</label>
      {children}
    </div>
  );
}

// ── Scoring ───────────────────────────────────────────────────────

interface Score {
  total:    number;
  status:   number;
  verdict:  number;
  bodySize: number;
  speed:    number;
  reason:   string;
}

/** scoreEngine — keep simple + visible. The user sees the breakdown
 *  in the matrix tooltip so they know exactly why an engine got its
 *  score. Adjust the weights here and the UI updates. Maximum
 *  achievable score is 100 (40 status + 30 verdict + 20 body + 10 speed). */
function scoreEngine(r: ProbeResult): Score {
  if (r.error) {
    return {
      total:    0,
      status:   0,
      verdict:  0,
      bodySize: 0,
      speed:    0,
      reason:   `${r.error.kind}: ${r.error.message.slice(0, 80)}`,
    };
  }
  const sc       = r.statusCode ?? 0;
  const status   = sc === 200 ? 40 : sc >= 200 && sc < 400 ? 20 : sc >= 400 && sc < 500 ? 10 : 0;
  const verdict  = r.blockVerdict === 'ok' ? 30 : r.blockVerdict === 'soft_block' ? 10 : 0;
  const bodySize = r.bodySizeBytes >= 10_000 ? 20 : r.bodySizeBytes >= 1_000 ? 10 : 0;
  const speed    = r.timing.total < 3_000 ? 10 : r.timing.total < 10_000 ? 5 : 0;
  const total    = status + verdict + bodySize + speed;
  const parts = [
    sc === 200 ? '200 OK' : `status ${sc}`,
    r.blockVerdict === 'ok' ? 'no block' : r.blockVerdict.replace('_', ' '),
    `${humanBytes(r.bodySizeBytes)} body`,
    `${r.timing.total}ms`,
  ];
  return { total, status, verdict, bodySize, speed, reason: parts.join(' · ') };
}

function pickRecommendation(results: ProbeResult[]): { winner: ProbeResult | null; rationale: string } {
  const scored = results
    .map((r) => ({ r, s: scoreEngine(r) }))
    .filter(({ s }) => s.total > 0);

  if (scored.length === 0) {
    return { winner: null, rationale: 'No engine produced a viable result. Investigate the target manually before onboarding.' };
  }
  // Sort: highest score first, then cheapest engine on ties.
  scored.sort((a, b) => {
    if (b.s.total !== a.s.total) return b.s.total - a.s.total;
    return COST_RANK.indexOf(a.r.engine) - COST_RANK.indexOf(b.r.engine);
  });
  const winner       = scored[0].r;
  const winnerScore  = scored[0].s;
  const margin       = scored[1] ? scored[0].s.total - scored[1].s.total : winnerScore.total;
  const rationale =
    winnerScore.total >= 80
      ? `Strong fit (${winnerScore.total}/100). ${winnerScore.reason}.`
      : winnerScore.total >= 50
        ? `Workable (${winnerScore.total}/100). ${winnerScore.reason}. Watch for soft-block escalation under volume.`
        : `Marginal (${winnerScore.total}/100). ${winnerScore.reason}. Likely needs proxy or browser fallback.`;
  const tieBreaker = margin <= 5 && scored[1]
    ? ` Edge over ${scored[1].r.engine} is small (${margin} pts) — preferred for being cheaper.`
    : '';
  return { winner, rationale: rationale + tieBreaker };
}

// ── Matrix ────────────────────────────────────────────────────────

function FeasibilityMatrix({ results }: { results: ProbeResult[] }) {
  const sorted = [...results].sort((a, b) => COST_RANK.indexOf(a.engine) - COST_RANK.indexOf(b.engine));
  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Feasibility matrix</h3>
        <span className="text-[11px] text-slate-500">{results.length} engines · scored 0-100</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <th className="px-3 py-2 font-medium">Engine</th>
              <th className="px-2 py-2 font-medium">Status</th>
              <th className="px-2 py-2 font-medium">Verdict</th>
              <th className="px-2 py-2 font-medium text-right">Score</th>
              <th className="px-2 py-2 font-medium text-right">Body</th>
              <th className="px-2 py-2 font-medium text-right">Time</th>
              <th className="px-2 py-2 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => <MatrixRow key={r.id} result={r} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MatrixRow({ result }: { result: ProbeResult }) {
  const score = scoreEngine(result);
  const cls = score.total >= 80
    ? 'bg-emerald-500/5'
    : score.total >= 50
      ? 'bg-amber-500/5'
      : score.total > 0
        ? 'bg-red-500/5'
        : 'bg-slate-900/30';
  return (
    <tr className={cn('border-b border-slate-800/60 hover:bg-slate-800/30 transition', cls)}>
      <td className="px-3 py-2 font-mono text-slate-200">{result.engine}</td>
      <td className="px-2 py-2 tabular-nums">
        {result.error
          ? <span className="text-red-300">{result.error.kind}</span>
          : (result.statusCode ?? '—')}
      </td>
      <td className="px-2 py-2">
        <span className={cn(
          'text-[10px] px-1 rounded',
          result.blockVerdict === 'ok'         ? 'bg-emerald-500/15 text-emerald-200' :
          result.blockVerdict === 'soft_block' ? 'bg-amber-500/15 text-amber-200' :
                                                  'bg-red-500/15 text-red-200',
        )}>
          {result.blockVerdict.replace('_', ' ')}
        </span>
      </td>
      <td className="px-2 py-2 tabular-nums text-right">
        <span className={cn(
          'font-medium',
          score.total >= 80 ? 'text-emerald-300' :
          score.total >= 50 ? 'text-amber-300' :
          score.total > 0   ? 'text-red-300'    :
                              'text-slate-500',
        )}>
          {score.total}
        </span>
      </td>
      <td className="px-2 py-2 tabular-nums text-right text-slate-400">
        {humanBytes(result.bodySizeBytes)}
      </td>
      <td className="px-2 py-2 tabular-nums text-right text-slate-400">
        {result.timing.total}ms
      </td>
      <td className="px-2 py-2 text-slate-400 text-[11px]">{score.reason}</td>
    </tr>
  );
}

// ── Recommendation ────────────────────────────────────────────────

function Recommendation({ results }: { results: ProbeResult[] }) {
  const { winner, rationale } = pickRecommendation(results);
  if (!winner) {
    return (
      <section className="border border-red-500/30 bg-red-500/10 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" aria-hidden />
        <div>
          <h3 className="text-sm font-semibold text-red-200">No viable engine</h3>
          <p className="text-xs text-red-200/80 mt-1">{rationale}</p>
        </div>
      </section>
    );
  }
  return (
    <section className="border border-emerald-500/30 bg-emerald-500/10 rounded-lg p-4 flex items-start gap-3">
      <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-emerald-200 flex items-center gap-2">
          Recommended engine: <code className="text-emerald-100">{winner.engine}</code>
        </h3>
        <p className="text-xs text-emerald-200/80 mt-1">{rationale}</p>
      </div>
    </section>
  );
}

// ── Empty state + roadmap ────────────────────────────────────────

function EmptyHint() {
  return (
    <div className="border border-dashed border-slate-800 rounded-lg p-8 text-center text-sm text-slate-500">
      <Sparkles className="w-6 h-6 mx-auto mb-2 text-slate-600" aria-hidden />
      Paste a URL above and click <strong className="text-slate-300">Run feasibility check</strong> —
      we'll fire it through all five engines in parallel and rank them.
    </div>
  );
}

/** Locked / disabled stage placeholder. Shown when an upstream
 *  stage hasn't produced its output yet. Same shape as the live
 *  stage card so the page layout doesn't shift when the lock
 *  releases. */
/** Locked-state placeholder for a downstream stage. The `status` pill
 *  distinguishes "ready, just gated on Stage 1 input" (live) from
 *  "roadmap, code not shipped yet" (soon) — important so an operator
 *  doesn't read every grayed-out card as "coming soon". Mirrors the
 *  ROADMAP table below. */
function LockedStage({ icon, title, hint, status }: {
  icon:   React.ReactNode;
  title:  string;
  hint:   string;
  status: 'live' | 'soon';
}) {
  const pill = status === 'live'
    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
    : 'bg-amber-500/10 text-amber-300 border-amber-500/30';
  return (
    <section className="border border-slate-800 bg-slate-900/20 rounded-lg p-4 opacity-60">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-slate-300">{title}</h3>
        <span className={cn('text-[10px] px-1.5 py-px rounded border uppercase tracking-wide', pill)}>
          {status}
        </span>
        <span className="text-[10px] px-1 rounded bg-slate-800 text-slate-400">locked</span>
      </div>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
    </section>
  );
}

const ROADMAP: ReadonlyArray<{ stage: number; name: string; status: 'live' | 'next' | 'soon'; what: string }> = [
  { stage: 1, name: 'Recon',    status: 'live', what: 'Score every engine on this URL.' },
  { stage: 2, name: 'Profile',  status: 'live', what: 'Auto-discover listing-card selector, cookie banner, JS-render need, evasion tier.' },
  { stage: 3, name: 'Generate', status: 'live', what: 'Render scraper code from working prod templates with the discovered config — download as zip.' },
  { stage: 4, name: 'PR',       status: 'live', what: 'Open a PR with the generated scraper, feasibility matrix, profile evidence, and cleaner-stub TODO baked into the body.' },
  { stage: 5, name: 'Register', status: 'live', what: 'Gates on Stage 4 PR merge, then writes scrapers_registry with derived runs_per_day. Cron coordinator picks it up next tick.' },
];

// ── B5 — Recent runs history panel ────────────────────────────────

const STAGE_LABEL: Record<OnboardStage, string> = {
  recon:    'Recon',
  profile:  'Profile',
  generate: 'Generate',
  pr:       'PR',
  done:     'Done',
  failed:   'Failed',
};

const STAGE_TONE: Record<OnboardStage, string> = {
  recon:    'bg-cyan-500/15 text-cyan-200 border-cyan-500/30',
  profile:  'bg-cyan-500/15 text-cyan-200 border-cyan-500/30',
  generate: 'bg-purple-500/15 text-purple-200 border-purple-500/30',
  pr:       'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  done:     'bg-emerald-500/20 text-emerald-100 border-emerald-500/40',
  failed:   'bg-red-500/15 text-red-200 border-red-500/30',
};

/** History panel — shows the last 20 onboard runs with stage badge,
 *  target URL, and PR link when available. Polls modestly (every
 *  30s) so a long-running pipeline mid-stage appears live without
 *  hammering the API. */
function RecentRunsPanel() {
  const q = useQuery({
    queryKey: ['onboardRuns'],
    queryFn:  () => listOnboardRuns({ limit: 20 }),
    refetchInterval: 30_000,
    // Don't show "loading" on every poll — only the first.
    refetchOnWindowFocus: false,
    // If the backend endpoint isn't deployed yet (e.g. mid-rollout
    // when Deploy Prod is failing), we still want to render the
    // locked-state placeholder rather than crash-loop the panel.
    retry: false,
  });

  // Header used by every state below. This panel is the audit-log
  // plumbing the (roadmap) Stage 5 — Live scheduler will eventually
  // write to. It's NOT itself Stage 5 — keep the title generic so
  // the user doesn't conflate "history of runs" with "scheduling
  // the merged scraper into the live fleet".
  const header = (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <FileText className="w-4 h-4 text-slate-400" aria-hidden />
        Run history
      </h3>
      <span className="text-[10px] text-slate-500">last 20 · refreshes every 30s</span>
    </div>
  );

  if (q.isLoading) {
    return (
      <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-2">
        {header}
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
          Loading recent runs…
        </div>
      </section>
    );
  }

  // Either errored (endpoint not yet on this prod build) or empty —
  // both render the same locked-state hint so the section is always
  // visible from the first page load. The hint copy distinguishes
  // them so an operator can tell "no runs yet" from "backend down".
  if (q.isError || !q.data || q.data.length === 0) {
    return (
      <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-2">
        {header}
        <p className="text-xs text-slate-500">
          {q.isError
            ? 'Run-history endpoint is unavailable on this build — likely mid-rollout. Will appear once the backend deploy clears.'
            : 'Runs you start in Stage 1 will appear here as they progress through Profile → Generate → Open PR. Each row links to its PR.'}
        </p>
      </section>
    );
  }

  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
      {header}
      <ul className="divide-y divide-slate-800">
        {q.data.map((r) => <RunRow key={r.id} run={r} />)}
      </ul>
    </section>
  );
}

// ── Stage 5 — pending-register history panel ─────────────────────
//
// Why this exists: an operator firing off back-to-back onboardings
// for several retailers used to lose the prior Stage 5 form the
// moment Recon for the next retailer started. The active stepper
// is single-tracked by design (one form, one runId in scope), so
// the persistence has to live in a panel that survives across
// active-form swaps. This panel polls the backend every 30s for
// every run still gated on a PR merge (or already done) and lets
// the operator register any that are ready without touching the
// active stepper.

function Stage5HistoryPanel() {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['onboardPendingRegister'],
    queryFn:  () => listPendingRegisterRuns(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    // Mid-rollout: render the empty hint rather than crash-loop.
    retry: false,
  });

  const header = (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-amber-300" aria-hidden />
        Stage 5 — Register &amp; schedule (history)
      </h3>
      <span className="text-[10px] text-slate-500">refreshes every 30s</span>
    </div>
  );

  if (q.isLoading) {
    return (
      <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-2">
        {header}
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
          Loading pending registrations…
        </div>
      </section>
    );
  }
  if (q.isError || !q.data || q.data.length === 0) {
    return (
      <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-2">
        {header}
        <p className="text-xs text-slate-500">
          {q.isError
            ? 'Pending-register endpoint is unavailable on this build — likely mid-rollout.'
            : 'No runs awaiting registration. Once Stage 4 opens a PR, the run will appear here with a live merge-status badge.'}
        </p>
      </section>
    );
  }

  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
      {header}
      <ul className="divide-y divide-slate-800">
        {q.data.map((r) => (
          <PendingRegisterRow
            key={r.id}
            run={r}
            onRegistered={() => queryClient.invalidateQueries({ queryKey: ['onboardPendingRegister'] })}
          />
        ))}
      </ul>
    </section>
  );
}

function PendingRegisterRow({
  run, onRegistered,
}: {
  run:          OnboardRun;
  onRegistered: () => void;
}) {
  const pr = run.pr;
  const registered         = !!pr?.registeredAt;
  const merged             = pr?.merged === true;
  const explicitlyUnmerged = pr?.merged === false;
  const checking           = pr?.merged == null;
  // CI rollup state from the backend poll. Distinct from merge state:
  // a PR can be open with failing CI (block until green) or merged with
  // its last pre-merge CI run still cached — we only colour CI-failed
  // when the PR is NOT yet merged, so a merged-but-old-failing PR
  // doesn't shout red after the operator has already moved on.
  const ciFailed   = !merged && pr?.ciStatus === 'fail';
  const ciPending  = !merged && pr?.ciStatus === 'pending';

  const registerM = useMutation({
    mutationFn: async () => {
      if (!pr) throw new Error('missing PR on run');
      return registerOnboardedScraper({
        sourceId:          run.sourceId ?? '',
        displayName:       run.displayName ?? run.sourceId ?? '',
        pageType:          (run.profile?.pageType ?? 'html') as 'html' | 'json',
        recommendedEngine: run.recon?.recommendedEngine ?? '',
        prNumber:          pr.number,
        runId:             run.id,
      });
    },
    onSuccess: () => onRegistered(),
  });

  // Stage-5 row actions — wired to backend/.../pr-actions.ts. Each one
  // invalidates the pending-register feed on success so the badge
  // re-renders from fresh poll state instead of an optimistic guess.
  const mergeM = useMutation({
    mutationFn: async () => {
      if (!pr) throw new Error('missing PR on run');
      await mergeOnboardPr(pr.number);
    },
    onSuccess: () => onRegistered(),
  });
  const closeM = useMutation({
    mutationFn: async () => {
      if (!pr) throw new Error('missing PR on run');
      await closeOnboardPr(pr.number);
    },
    onSuccess: () => onRegistered(),
  });
  const rerunM = useMutation({
    mutationFn: async () => {
      if (!pr) throw new Error('missing PR on run');
      return rerunOnboardPrCi(pr.number);
    },
    onSuccess: () => onRegistered(),
  });
  const anyActionPending = mergeM.isPending || closeM.isPending || rerunM.isPending;

  // Pick a badge state. Priority, top-to-bottom:
  //   registered  → terminal good (registry row written)
  //   merged      → "ready to register" (action button on the row)
  //   ci-failed   → red — PR can't merge until fixed
  //   ci-pending  → blue — CI still in flight
  //   open w/o CI signal yet → "awaiting merge"
  //   pre-poll    → "checking…" (only until first poll lands)
  // Coloured to match the existing Stage-5 badge so the eye-line
  // carries between this panel and the active form.
  let badge: { text: string; cls: string };
  if (registered)              badge = { text: 'registered',         cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30' };
  else if (merged)             badge = { text: 'ready to register',  cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30 animate-pulse' };
  else if (ciFailed)           badge = { text: 'CI failed',          cls: 'bg-red-500/15 text-red-200 border-red-500/30' };
  else if (ciPending)          badge = { text: 'CI running',         cls: 'bg-sky-500/15 text-sky-200 border-sky-500/30' };
  else if (explicitlyUnmerged) badge = { text: 'awaiting merge',     cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' };
  else                         badge = { text: 'checking…',          cls: 'bg-slate-700/50 text-slate-300 border-slate-600' };
  void checking; // for clarity in branch reading

  return (
    <li className="py-2 flex items-center gap-3 text-xs">
      <span className={cn('px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide font-mono', badge.cls)}>
        {badge.text}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-slate-200 truncate">
          {run.displayName || run.sourceId || run.targetUrl}
          {run.sourceId && (
            <span className="text-[10px] text-slate-500 ml-1.5 font-mono">{run.sourceId}</span>
          )}
        </div>
        <div className="text-[10px] text-slate-500 truncate">
          <span title={run.targetUrl}>{run.targetUrl}</span>
          {pr?.lastCheckedAt && (
            <>
              {' · '}
              <span title={pr.lastCheckedAt}>checked {relTime(pr.lastCheckedAt)}</span>
            </>
          )}
        </div>
      </div>
      {pr?.url && (
        <a href={pr.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-200 hover:text-emerald-100 underline shrink-0">
          <ExternalLink className="w-3 h-3" aria-hidden />
          PR #{pr.number}
        </a>
      )}
      {/* PR actions — visible only on rows where the operator can
          plausibly act. Re-run CI only fires when CI actually failed;
          merge / close stay open until the PR resolves. */}
      {pr && !registered && !explicitlyUnmerged && ciFailed && (
        <button type="button"
          onClick={() => rerunM.mutate()}
          disabled={anyActionPending}
          title="Re-run failed CI jobs"
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-sky-500/15 border border-sky-500/30 text-sky-200 hover:bg-sky-500/25 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
          {rerunM.isPending
            ? <><Loader2 className="w-3 h-3 animate-spin" aria-hidden /> Rerunning…</>
            : <><RefreshCw className="w-3 h-3" aria-hidden /> Rerun CI</>}
        </button>
      )}
      {pr && !registered && !merged && pr.merged === false && (
        <button type="button"
          onClick={() => mergeM.mutate()}
          disabled={anyActionPending || ciFailed}
          title={ciFailed ? 'Fix CI before merging' : 'Squash-merge this PR'}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
          {mergeM.isPending
            ? <><Loader2 className="w-3 h-3 animate-spin" aria-hidden /> Merging…</>
            : <><GitMerge className="w-3 h-3" aria-hidden /> Merge</>}
        </button>
      )}
      {pr && !registered && !merged && pr.merged === false && (
        <button type="button"
          onClick={() => closeM.mutate()}
          disabled={anyActionPending}
          title="Close PR without merging"
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-slate-700/40 border border-slate-600 text-slate-200 hover:bg-slate-700/60 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
          {closeM.isPending
            ? <><Loader2 className="w-3 h-3 animate-spin" aria-hidden /> Closing…</>
            : <><GitPullRequestClosed className="w-3 h-3" aria-hidden /> Close</>}
        </button>
      )}
      {merged && !registered && (
        <button type="button"
          onClick={() => registerM.mutate()}
          disabled={registerM.isPending || anyActionPending}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
          {registerM.isPending
            ? <><Loader2 className="w-3 h-3 animate-spin" aria-hidden /> Registering…</>
            : <><CalendarClock className="w-3 h-3" aria-hidden /> Register</>}
        </button>
      )}
      {registered && (
        <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" aria-hidden />
      )}
      {Boolean(registerM.error) && (
        <span className="text-[10px] text-red-300 truncate max-w-[12rem]" title={(registerM.error as Error).message}>
          {(registerM.error as Error).message}
        </span>
      )}
      {Boolean(mergeM.error || closeM.error || rerunM.error) && (
        <span className="text-[10px] text-red-300 truncate max-w-[12rem]"
          title={(mergeM.error || closeM.error || rerunM.error as Error)?.message}>
          {(mergeM.error || closeM.error || rerunM.error as Error)?.message}
        </span>
      )}
    </li>
  );
}

function RunRow({ run }: { run: OnboardRunSummary }) {
  return (
    <li className="py-2 flex items-center gap-3 text-xs">
      <span className={cn(
        'px-1.5 py-0.5 rounded border text-[10px] font-medium tabular-nums',
        STAGE_TONE[run.stage],
      )}>
        {STAGE_LABEL[run.stage]}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-slate-200 truncate">
          {run.displayName || run.sourceId || run.targetUrl}
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1.5 truncate">
          <span title={run.targetUrl} className="truncate">{run.targetUrl}</span>
          <span>·</span>
          <span title={run.updatedAt}>{relTime(run.updatedAt)}</span>
          {run.errorMsg && (
            <>
              <span>·</span>
              <span className="text-red-300 truncate" title={run.errorMsg}>
                error in {run.errorStage ?? 'pipeline'}: {run.errorMsg}
              </span>
            </>
          )}
        </div>
      </div>
      {run.prUrl && (
        <a
          href={run.prUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-200 hover:text-emerald-100 underline shrink-0"
        >
          <ExternalLink className="w-3 h-3" aria-hidden />
          PR #{run.prNumber}
        </a>
      )}
    </li>
  );
}

/** Compact relative-time formatter — keeps the row tight without
 *  pulling in date-fns just for this one place. */
function relTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60)        return `${s}s ago`;
  if (s < 3600)      return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400)    return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86_400)}d ago`;
}

function RoadmapCards() {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold text-slate-300 flex items-center gap-2">
        <ChevronRight className="w-3 h-3" aria-hidden /> Pipeline roadmap
      </h3>
      <ol className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {ROADMAP.map((s) => (
          <li
            key={s.stage}
            className={cn(
              'border rounded-md p-2.5 text-xs',
              s.status === 'live' && 'border-emerald-500/40 bg-emerald-500/5',
              s.status === 'next' && 'border-slate-700 bg-slate-900/40',
              s.status === 'soon' && 'border-slate-800 bg-slate-900/20 opacity-60',
            )}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] text-slate-500 tabular-nums">stage {s.stage}</span>
              <span className={cn(
                'text-[10px] px-1 rounded',
                s.status === 'live' && 'bg-emerald-500/20 text-emerald-200',
                s.status === 'next' && 'bg-slate-700 text-slate-200',
                s.status === 'soon' && 'bg-slate-800 text-slate-400',
              )}>
                {s.status}
              </span>
            </div>
            <div className="text-sm font-medium text-slate-200 flex items-center gap-1">
              {s.name}
              {s.status === 'live' && <ArrowRight className="w-3 h-3 text-emerald-300" aria-hidden />}
            </div>
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{s.what}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ── Stage 2 — Profile section ─────────────────────────────────────

const PROFILE_STORAGE_KEY = 'otr.onboard.profiles';

interface SavedProfile {
  url:     string;
  savedAt: string;
  profile: SiteProfile;
}

function loadSavedProfiles(): SavedProfile[] {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(window.localStorage.getItem(PROFILE_STORAGE_KEY) ?? '[]'); }
  catch { return []; }
}

/** Stage-2 card. Visible after Recon completes. Click 'Profile this
 *  site' to:
 *    1. Pick the best HTTP + best browser ProbeResult from the
 *       feasibility matrix.
 *    2. Stream the rendered HTML artefact for the browser result
 *       (already in GridFS via /artefact/:id, fetched through the
 *       auth-friendly blob bridge).
 *    3. Run all the heuristics in JS on the captured HTML.
 *    4. Render the discovered profile + a Save button (localStorage
 *       for now; server-side persistence lands in B5). */
function ProfileSection({
  url,
  results,
  pageType,
  pageTypeOverride,
  productsPath,
  recordStage,
  onPipelineComplete,
}: {
  url:               string;
  results:           ProbeResult[];
  /** Effective page type after auto-detect resolution. */
  pageType:          'html' | 'json';
  /** What the operator actually selected ('auto' | 'html' | 'json'),
   *  used purely to render a "(auto-detected)" hint when applicable. */
  pageTypeOverride:  PageType;
  /** Operator-supplied JMESPath / dot-path to the products array.
   *  Empty string = let the heuristic guess. Only used when
   *  `pageType === 'json'`. */
  productsPath:      string;
  /** B5 — best-effort persistence callback. No-ops when no runId. */
  recordStage:       (patch: Parameters<typeof patchOnboardRun>[1]) => Promise<void>;
  /** Called after a successful Stage-4 PR open. Top-level clears all
   *  Recon / Profile / Generate state so the operator can immediately
   *  onboard another retailer. */
  onPipelineComplete: () => void;
}) {
  const [profile, setProfile] = useState<SiteProfile | null>(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [saved,   setSaved]   = useState(false);

  const recommended = pickRecommendation(results).winner;
  const httpResult = results.find((r) => r.engine === 'direct_http' || r.engine === 'docker_http' || r.engine === 'cf_http');
  const browserResult = results.find((r) => r.engine === 'docker_browser' || r.engine === 'cf_browser');

  async function profileNow() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      // Pull rendered HTML from GridFS for the browser result.
      // bodyPreview is capped at 4KB which is too small for reliable
      // selector heuristics — but the renderedHtml artefact is the
      // FULL post-JS DOM, perfect for this.
      let browserHtml = '';
      const revokes: Array<() => void> = [];
      if (browserResult?.renderedHtmlId) {
        const a = await fetchArtefact(browserResult.renderedHtmlId);
        revokes.push(a.revoke);
        // The artefact is served as text/html; read it via fetch
        // again (cheaper than stream-decode in the browser).
        const r = await fetch(a.url);
        browserHtml = await r.text();
      }

      // For JSON profiles the bodyPreview cap (4KB) is almost always
      // too small — most product-search APIs return 20–200KB payloads.
      // Pull the full HTTP body from GridFS (`bodyFullId`, same auth-
      // gated artefact endpoint as renderedHtmlId) when available.
      // Fall back to bodyPreview for HTML-mode or when bodyFullId is
      // missing (shouldn't happen for successful HTTP probes).
      let httpBody = httpResult?.bodyPreview ?? '';
      if (pageType === 'json' && httpResult?.bodyFullId) {
        try {
          const a = await fetchArtefact(httpResult.bodyFullId);
          revokes.push(a.revoke);
          const r = await fetch(a.url);
          httpBody = await r.text();
        } catch {
          // Best-effort — fall back to bodyPreview if the full body
          // can't be fetched (e.g. artefact garbage-collected).
        }
      }

      const built = buildProfile({
        url,
        recommendedEngine: recommended?.engine ?? '',
        httpBody,
        browserHtml,
        blockVerdict:      recommended?.blockVerdict ?? 'ok',
        blockSignals:      recommended?.blockSignals ?? [],
        pageType,
        productsPath:      productsPath || undefined,
      });
      setProfile(built);
      for (const r of revokes) r();
      // B5 — persist Stage 2 progress to the run history.
      void recordStage({
        stage: 'profile',
        profile: {
          completeness:         built.completeness,
          listingSelector:      built.listingCard?.selector,
          cookieBannerProvider: built.cookieBanner?.provider,
          cookieAcceptSelector: built.cookieBanner?.acceptSelector,
          jsRenderRequired:     built.jsRenderNeed.required,
          paginationType:       built.pagination?.type,
          paginationParam:      built.pagination?.paramName,
        },
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function saveProfile() {
    if (!profile) return;
    const all = loadSavedProfiles();
    // Replace any existing profile for the same URL — keeps the
    // localStorage from growing unboundedly when re-profiling.
    const next: SavedProfile[] = [
      ...all.filter((p) => p.url !== profile.url),
      { url: profile.url, savedAt: new Date().toISOString(), profile },
    ];
    window.localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(next));
    setSaved(true);
    // Clear the "saved" badge after 3s so re-saves can re-flash it.
    setTimeout(() => setSaved(false), 3_000);
  }

  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Beaker className="w-4 h-4 text-cyan-300" aria-hidden /> Stage 2 — Profile
            <span className={cn(
              'text-[10px] px-1.5 py-px rounded border uppercase tracking-wide font-mono',
              pageType === 'json'
                ? 'bg-purple-500/15 text-purple-200 border-purple-500/30'
                : 'bg-slate-700/50 text-slate-300 border-slate-600',
            )}>
              {pageType}
            </span>
            {pageTypeOverride === 'auto' && (
              <span className="text-[10px] text-slate-500 normal-case">(auto-detected)</span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {pageType === 'json'
              ? 'JSON API — heuristics extract the products array via your path, then sample item keys (id/title/price candidates).'
              : 'Auto-discover scraper config from what Recon already captured. No extra network calls.'}
          </p>
        </div>
        <button
          type="button"
          onClick={profileNow}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded',
            'bg-cyan-500/15 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/25',
            'disabled:opacity-50 disabled:cursor-not-allowed transition',
          )}
        >
          {busy
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Profiling…</>
            : <><Wand2 className="w-3.5 h-3.5" aria-hidden /> {profile ? 'Re-profile' : 'Profile this site'}</>}
        </button>
      </div>

      {err && (
        <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
          {err}
        </div>
      )}

      {profile && (
        <>
          <ProfileCard profile={profile} onSave={saveProfile} saved={saved} />
          <GenerateSection
            profile={profile}
            url={url}
            results={results}
            recordStage={recordStage}
            onPipelineComplete={onPipelineComplete}
          />
        </>
      )}
    </section>
  );
}

// ── Stage 3 — Generate ────────────────────────────────────────────

interface GenerateForm {
  sourceId:           string;
  displayName:        string;
  urlPattern:         string;
  defaultSearchTerms: string;  // comma-joined for the textarea; split on submit
  defaultMaxPage:     number;
  /** Recon recommends an engine; operator can override here. The chosen
   *  engine drives the template family and which selector fields are
   *  validated server-side. */
  engine:             'cf_http' | 'docker_browser' | 'docker_http';
  /** HTML selectors used by the cf_http_html template. Pre-filled from
   *  the Profile-stage heuristics; operator can hand-edit. Empty
   *  string = "don't extract this field". `listingSelector` is the
   *  only one the server hard-requires for cf_http. */
  listingSelector:    string;
  titleSelector:      string;
  priceSelector:      string;
  urlSelector:        string;
  idSelector:         string;
  /** Pagination configuration — drives the worker's buildURL math.
   *  Auto-detected from the URL in deriveInitialGenerateForm; operator
   *  can override before submit.
   *  - paginationType: page → param value = page + base
   *                    offset → param value = page * multiplier
   *                    cursor → param value passed through verbatim
   *  - paginationParam: query key the worker writes to (e.g. 'page',
   *                     'offset', '_pgn').
   *  - paginationBase:  first-page number. 1 for most sites, 0 for
   *                     offset-style or for sites that 0-index pages.
   *  - paginationMultiplier: page size for offset-style ('size=48' →
   *                          multiplier=48 so offsets are 0,48,96…). */
  paginationType:       'page' | 'offset' | 'cursor';
  paginationParam:      string;
  paginationBase:       number;
  paginationMultiplier: number;
}

function deriveInitialGenerateForm(profile: SiteProfile): GenerateForm {
  // Best-effort derivation. Operator can edit before submitting.
  const host = safeHostFromUrl(profile.url);
  const baseId = host.replace(/\./g, '_').replace(/[^a-z0-9_]/gi, '_').toLowerCase();
  // Try to back out the search term from the URL so the URL pattern
  // has a {term} placeholder. Heuristic: look at common query keys.
  const urlPattern = inferUrlPattern(profile.url);

  // Pick the engine: Recon's pick wins, but constrain to the three
  // engines the generator's dispatcher actually supports. Anything
  // unrecognised (e.g. the legacy `direct_http`) falls back to cf_http
  // since it's the lightest-touch HTML engine.
  const engineFromProfile = profile.recommendedEngine;
  const engine: GenerateForm['engine'] =
    engineFromProfile === 'docker_browser' ? 'docker_browser'
    : engineFromProfile === 'docker_http' ? 'docker_http'
    : 'cf_http';

  // Source-id suffix mirrors the engine choice so the cron coordinator
  // routes the registry row to the right family without manual edits.
  const idSuffix = engine === 'cf_http' ? '' : '_docker';
  const sourceId = baseId ? `${baseId}${idSuffix}` : `new_source${idSuffix}`;

  const card = profile.listingCard;
  // Pagination is opt-in: only pre-fill when detection found something
  // in the URL or the Profile stage. Leaving paginationParam empty
  // means "no pagination" — buildBody skips the URL rewrite + skips
  // emitting the pagination fields server-side, so the worker just
  // fetches one page per term (correct for single-page endpoints).
  // The operator can hand-fill the param later if they want paging.
  const pag = inferPaginationFromUrl(profile.url, profile.pagination);
  return {
    sourceId,
    displayName:        host || 'New source',
    urlPattern,
    defaultSearchTerms: DEFAULT_SEARCH_TERMS_CSV,
    defaultMaxPage:     2,
    engine,
    listingSelector:    card?.selector ?? '',
    titleSelector:      card?.titleSelector ?? '',
    priceSelector:      card?.priceSelector ?? '',
    urlSelector:        card?.urlSelector ?? '',
    idSelector:         card?.idSelector ?? '',
    paginationType:       pag?.type       ?? 'page',
    paginationParam:      pag?.param      ?? '',
    paginationBase:       pag?.base       ?? 1,
    paginationMultiplier: pag?.multiplier ?? 1,
  };
}

/** Detect pagination config from the live URL + Profile-stage signals.
 *  Used to pre-fill the Stage-3 form so the operator doesn't have to
 *  re-derive offset / multiplier / base by hand. Falls back to sensible
 *  defaults (page-style, 'page' param, base=1, multiplier=1) when
 *  nothing in the URL matches.
 *
 *  Offset detection: if the URL has any of (offset, start, from, skip)
 *  AND a sibling size hint (size, limit, per_page, pagesize, rows), we
 *  read both — type=offset, multiplier=<size>. Otherwise we look at
 *  profile.pagination.type for cursor/offset signals from Stage-2.
 */
function inferPaginationFromUrl(
  raw: string,
  profilePag: SiteProfile['pagination'],
): { type: GenerateForm['paginationType']; param: string; base: number; multiplier: number } | null {
  let url: URL;
  try { url = new URL(raw); } catch { return null; }

  const OFFSET_KEYS = ['offset', 'start', 'from', 'skip'];
  const SIZE_KEYS   = ['size', 'limit', 'per_page', 'pagesize', 'rows', 'count'];
  const PAGE_KEYS   = ['page', '_pgn', 'p', 'pg', 'pagenum', 'pageno'];
  const CURSOR_KEYS = ['cursor', 'next_token', 'pagetoken', 'continuation'];

  const haveKey = (keys: string[]): string | null => {
    for (const k of keys) {
      // Case-insensitive match against actual URL keys to preserve
      // operator-original casing in the output template.
      for (const [actual] of url.searchParams.entries()) {
        if (actual.toLowerCase() === k.toLowerCase()) return actual;
      }
    }
    return null;
  };

  const offsetParam = haveKey(OFFSET_KEYS);
  if (offsetParam) {
    let mult = 1;
    const sizeParam = haveKey(SIZE_KEYS);
    if (sizeParam) {
      const v = Number(url.searchParams.get(sizeParam));
      if (Number.isFinite(v) && v > 0) mult = v;
    }
    return { type: 'offset', param: offsetParam, base: 0, multiplier: mult };
  }

  const cursorParam = haveKey(CURSOR_KEYS);
  if (cursorParam) {
    return { type: 'cursor', param: cursorParam, base: 0, multiplier: 1 };
  }

  const pageParam = haveKey(PAGE_KEYS);
  if (pageParam) {
    return { type: 'page', param: pageParam, base: 1, multiplier: 1 };
  }

  // Nothing matched in the query — fall back to Profile signals.
  if (profilePag?.type === 'offset-param') {
    return {
      type: 'offset',
      param: profilePag.paramName || 'offset',
      base: 0,
      multiplier: profilePag.multiplier ?? 1,
    };
  }
  if (profilePag?.type === 'cursor-param') {
    return { type: 'cursor', param: profilePag.paramName || 'cursor', base: 0, multiplier: 1 };
  }
  if (profilePag?.type === 'page-param') {
    return { type: 'page', param: profilePag.paramName || 'page', base: 1, multiplier: 1 };
  }
  // Nothing matched and Profile didn't surface a pagination signal —
  // return null so the form leaves paginationParam empty (= disabled).
  return null;
}

function safeHostFromUrl(raw: string): string {
  try {
    return new URL(raw).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Build a URL pattern with {term} (and optionally {page}) placeholders.
 *  Looks for common search-term query params and pagination params,
 *  swapping the value for the placeholder when found. The operator
 *  can edit before submitting if our guess is off. */
function inferUrlPattern(raw: string): string {
  try {
    const u = new URL(raw);
    // Known query keys that commonly carry the search term across the
    // long tail of retailer URLs. The list is intentionally generous —
    // false-positives are harmless (operator can edit before submit),
    // false-negatives forced the heuristic to fall through to the
    // dumb-append branch below, which dropped a stray `&q={term}` at
    // the end of the URL (ebuyer.com uses `descriptionfilter=` so this
    // bit; the resulting pattern had {term} once in the real slot
    // *and* a garbage `q={term}` trailing in the fragment).
    const TERM_KEYS = [
      'q', 'query', 'search', 'k', '_nkw', 'keyword', 'keywords',
      'term', 'terms', 's', 'text', 'searchtext', 'searchterm',
      'descriptionfilter',                // ebuyer.com
      'searchString', 'search_query',     // common .NET / Rails naming
      'wd',                               // Baidu-style
    ];
    const TERM_KEYS_LOWER = new Set(TERM_KEYS.map((k) => k.toLowerCase()));
    const PAGE_KEYS = ['page', '_pgn', 'p', 'pg'];
    let foundTerm = false;
    // Case-insensitive lookup — URL query keys are case-sensitive in
    // theory but many sites accept either form. Iterate the URL's
    // actual keys to preserve the operator's original casing.
    for (const [k] of u.searchParams.entries()) {
      if (TERM_KEYS_LOWER.has(k.toLowerCase())) {
        u.searchParams.set(k, '{term}');
        foundTerm = true;
        break;
      }
    }
    for (const k of PAGE_KEYS) {
      if (u.searchParams.has(k)) {
        u.searchParams.set(k, '{page}');
        break;
      }
    }
    let out = u.toString();
    // searchParams.set escapes braces; restore them.
    out = out.replace(/%7B/g, '{').replace(/%7D/g, '}');
    if (!foundTerm) {
      // No recognised query key — try a path-segment swap:
      // /search/iphone-15 → /search/{term}
      out = out.replace(/\/search\/[^/?#]+/i, '/search/{term}');
      // Intentionally NOT appending `&q={term}` here any more. The old
      // behaviour produced garbage when the URL already had a term key
      // we didn't recognise (e.g. ebuyer's `descriptionfilter=`): the
      // appended `&q={term}` ended up *inside the fragment* and the
      // operator's hand-edited {term} duplicated it. The form's
      // pre-flight check rejects missing {term} with a clear error,
      // which is friendlier than us guessing wrong.
    }
    return out;
  } catch {
    return raw;
  }
}

// ── Stage 5 — Register & schedule ─────────────────────────────────

/** Mirror of backend/onboard/schedule-defaults.ts. Kept in lock-step
 *  so the override input renders the same default the server would
 *  derive. If you change one, change both — there's no auto-sync. */
function defaultRunsPerDayClient(pageType: 'html' | 'json', engine: string): number {
  if (pageType === 'json') return 24;
  if (engine === 'direct_http' || engine === 'docker_http') return 24;
  if (engine === 'cf_http')                                  return 16;
  if (engine === 'cf_browser' || engine === 'docker_browser') return 8;
  return 8;
}

function defaultExecutorClient(engine: string): string {
  if (engine === 'direct_http' || engine === 'cf_http' || engine === 'cf_browser') return 'cf_workers';
  return 'docker';
}

const RUNS_PER_DAY_PRESETS = [1, 2, 4, 8, 16, 24, 48] as const;

interface Stage5Props {
  pr:                OpenPRResult;
  sourceId:          string;
  displayName:       string;
  pageType:          'html' | 'json';
  recommendedEngine: string;
  recordStage:       (patch: Parameters<typeof patchOnboardRun>[1]) => Promise<void>;
  /** Optional onboard_runs id this Stage 5 belongs to. Forwarded to
   *  the register API so the backend can flip pr.registeredAt on
   *  the originating run (drives the Stage-5 history-panel badge).
   *  Also used as React `key` upstream so the form's local state
   *  (runsPerDay, merged-poll status) doesn't get clobbered when
   *  the operator starts onboarding a different retailer. */
  runId?:            string | null;
}

/** Stage 5 — gates on the Stage-4 PR being merged into main. Polls
 *  /pr-state every 30s after mount, auto-unlocks on merge. Operator
 *  can override the cadence (snapped to RUNS_PER_DAY_PRESETS) before
 *  hitting Register. The button still fires the API even when locked
 *  so the server-side merge guard is exercised — the popup surfaces
 *  the 409. */
function Stage5Register({ pr, sourceId, displayName, pageType, recommendedEngine, recordStage, runId: runIdProp }: Stage5Props) {
  const ctxRunId = useContext(RunIdContext);
  const runId = runIdProp ?? ctxRunId ?? null;
  const derivedRuns = defaultRunsPerDayClient(pageType, recommendedEngine);
  const derivedExec = defaultExecutorClient(recommendedEngine);
  const [runsPerDay, setRunsPerDay] = useState<number>(derivedRuns);
  const [merged, setMerged] = useState<boolean | null>(null);
  const [lastChecked, setLastChecked] = useState<string | null>(null);
  const [showLockedPopup, setShowLockedPopup] = useState(false);

  // Poll PR-state every 30s. Stops once merged. Initial fetch fires
  // on mount so the lock visibly resolves quickly when the operator
  // arrives at Stage 5 after already merging in another tab.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    async function tick() {
      try {
        const s = await getOnboardingPRState(pr.number);
        if (cancelled) return;
        setLastChecked(new Date().toISOString());
        setMerged(s.merged);
        if (s.merged && timer) { clearInterval(timer); timer = null; }
      } catch {
        // Silent — surface in the manual click path instead.
      }
    }
    void tick();
    timer = setInterval(tick, 30_000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [pr.number]);

  const registerM = useMutation({
    mutationFn: async () => {
      return registerOnboardedScraper({
        sourceId,
        displayName,
        pageType,
        recommendedEngine,
        prNumber: pr.number,
        runsPerDayOverride: runsPerDay !== derivedRuns ? runsPerDay : undefined,
        runId: runId ?? undefined,
      });
    },
    onSuccess: (result) => {
      void recordStage({ stage: 'done', sourceId: result.id, displayName: result.displayName });
    },
    onError: (e: unknown) => {
      if (e instanceof PrNotMergedError) {
        setShowLockedPopup(true);
        return;
      }
      void recordStage({ stage: 'failed', error: { stage: 'pr', message: (e as Error).message ?? 'unknown' } });
    },
  });

  const locked = merged !== true;

  return (
    <div className="border-t border-slate-800 pt-3 space-y-3 mt-3">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarClock className="w-4 h-4 text-amber-300" aria-hidden />
        <h4 className="text-sm font-semibold">Stage 5 — Register &amp; schedule</h4>
        <span className={cn(
          'text-[10px] px-1.5 py-px rounded border uppercase tracking-wide font-mono',
          merged === true
            ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30'
            : merged === false
              ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'
              : 'bg-slate-700/50 text-slate-300 border-slate-600',
        )}>
          {merged === true ? 'pr merged' : merged === false ? 'awaiting merge' : 'checking…'}
        </span>
        {lastChecked && (
          <span className="text-[10px] text-slate-500">
            checked {new Date(lastChecked).toLocaleTimeString()}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Once <a href={pr.url} target="_blank" rel="noopener noreferrer"
          className="text-emerald-300 hover:text-emerald-200 underline">PR #{pr.number}</a>{' '}
        merges into <code className="text-slate-300">main</code>, this button writes a row into{' '}
        <code className="text-slate-300">scrapers_registry</code> with the derived cadence.
        The cron coordinator picks it up on the next 30-min tick.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Field2 label="Executor (auto)">
          <input value={derivedExec} disabled className="input w-full font-mono text-xs opacity-70" />
        </Field2>
        <Field2 label="runs/day (snap to preset)">
          <select value={runsPerDay} onChange={(e) => setRunsPerDay(Number(e.target.value))}
            className="input w-full text-xs">
            {RUNS_PER_DAY_PRESETS.map((n) => (
              <option key={n} value={n}>{n}{n === derivedRuns ? ' (default)' : ''}</option>
            ))}
          </select>
        </Field2>
        <Field2 label="ticks_per_run (≈ every)">
          <input value={`${Math.max(1, Math.floor(48 / runsPerDay))} × 30min ≈ ${(24 / runsPerDay).toFixed(1)}h`}
            disabled className="input w-full text-xs opacity-70 font-mono" />
        </Field2>
      </div>

      {/* useMutation typed error as `unknown`, so the truthy check below
          must collapse to a boolean (Boolean(...)) before TSX accepts
          the conditional as ReactNode. */}
      {Boolean(registerM.error) && !(registerM.error instanceof PrNotMergedError) && (
        <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
          {(registerM.error as Error).message}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={() => registerM.mutate()}
          disabled={registerM.isPending || registerM.isSuccess}
          title={locked ? 'PR not merged yet — click anyway to verify' : 'Register & schedule'}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition',
            locked
              ? 'bg-slate-700/50 border border-slate-600 text-slate-300 hover:bg-slate-700'
              : 'bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}>
          {registerM.isPending
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Registering…</>
            : registerM.isSuccess
              ? <><CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Registered</>
              : <><CalendarClock className="w-3.5 h-3.5" aria-hidden /> Register &amp; schedule {locked ? '(locked)' : ''}</>}
        </button>
      </div>

      {registerM.isSuccess && registerM.data && (
        <div className="border border-emerald-500/30 bg-emerald-500/10 rounded p-3 text-xs text-emerald-200 space-y-1">
          <div className="flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
            <span>Scheduled — id <code>{registerM.data.id}</code> · {registerM.data.runsPerDay}/day · executor <code>{registerM.data.executor}</code></span>
          </div>
          <div className="text-emerald-200/70">
            Next tick: cron coordinator polls <code>scrapers_registry</code> every 30 min.
            Adjust later from the Scrapers tab.
          </div>
        </div>
      )}

      {showLockedPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowLockedPopup(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-amber-500/40 rounded-lg p-5 max-w-md mx-4 space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-300" aria-hidden />
              <h3 className="text-sm font-semibold text-amber-200">PR not merged yet</h3>
            </div>
            <p className="text-xs text-slate-300">
              Server-side check refused the registry write because{' '}
              <a href={pr.url} target="_blank" rel="noopener noreferrer"
                className="text-emerald-300 hover:text-emerald-200 underline">PR #{pr.number}</a>{' '}
              is still <strong>open</strong>. Merge it into <code className="text-slate-200">main</code>{' '}
              first, then come back — the button will auto-unlock within 30 seconds.
            </p>
            <div className="flex justify-end">
              <button type="button" onClick={() => setShowLockedPopup(false)}
                className="px-3 py-1.5 text-xs rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stage 3 + 4 — JSON-API generator + PR ─────────────────────────

interface JsonGenerateForm {
  sourceId:           string;
  displayName:        string;
  urlPattern:         string;
  defaultSearchTerms: string;
  defaultMaxPage:     number;
  listPath:           string;
  titleKey:           string;
  priceKey:           string;
  urlKey:             string;
  idKey:              string;
  paginationParam:    string;
  paginationType:     'page' | 'offset' | 'cursor';
}

function deriveJsonForm(profile: SiteProfile, _url: string): JsonGenerateForm {
  const host = safeHostFromUrl(profile.url);
  const baseId = host.replace(/\./g, '_').replace(/[^a-z0-9_]/gi, '_').toLowerCase();
  const pattern = inferUrlPattern(profile.url);
  const j = profile.json;
  return {
    sourceId:           baseId || 'new_source',
    displayName:        host || 'New JSON source',
    urlPattern:         pattern,
    defaultSearchTerms: DEFAULT_SEARCH_TERMS_CSV,
    defaultMaxPage:     2,
    listPath:           j?.listPath ?? '',
    titleKey:           j?.fieldGuess.title ?? '',
    priceKey:           j?.fieldGuess.price ?? '',
    urlKey:             j?.fieldGuess.url   ?? '',
    idKey:              j?.fieldGuess.id    ?? '',
    paginationParam:    profile.pagination?.paramName ?? 'page',
    paginationType:     profile.pagination?.type === 'offset-param' ? 'offset'
                       : profile.pagination?.type === 'cursor-param' ? 'cursor'
                       : 'page',
  };
}

function JsonGenerateSection({
  profile,
  url,
  results,
  recordStage,
  onPipelineComplete,
}: {
  profile:     SiteProfile;
  url:         string;
  results:     ProbeResult[];
  recordStage: (patch: Parameters<typeof patchOnboardRun>[1]) => Promise<void>;
  onPipelineComplete: () => void;
}) {
  const initial = useMemo(() => deriveJsonForm(profile, url), [profile, url]);
  const [form, setForm] = useState<JsonGenerateForm>(initial);
  const update = <K extends keyof JsonGenerateForm>(k: K, v: JsonGenerateForm[K]) =>
    setForm({ ...form, [k]: v });

  function buildBody() {
    const terms = form.defaultSearchTerms.split(/[,;\n]+/).map((t) => t.trim()).filter(Boolean);
    if (terms.length === 0) throw new Error('At least one default search term is required');
    if (!form.urlPattern.includes('{term}')) throw new Error('URL pattern must contain a {term} placeholder');
    return {
      sourceId:           form.sourceId,
      displayName:        form.displayName,
      urlPattern:         form.urlPattern,
      // Listing selectors / humanevasion are HTML-only but the wire
      // shape requires them — pass empty/cdp so the validator's
      // backward-compat branch doesn't trip on JSON requests.
      listingSelectors:   [],
      humanevasionTier:   'cdp' as const,
      defaultSearchTerms: terms,
      recommendedEngine:  profile.recommendedEngine,
      defaultMaxPage:     form.defaultMaxPage,
      pageType:           'json' as const,
      json: {
        listPath:        form.listPath,
        titleKey:        form.titleKey,
        priceKey:        form.priceKey,
        urlKey:          form.urlKey,
        idKey:           form.idKey,
        paginationParam: form.paginationParam || undefined,
        paginationType:  form.paginationType,
      },
    };
  }

  function buildFeasibilityMatrix(): PRFeasibilityRow[] {
    return results.map((r) => ({
      engine: r.engine,
      ok:     !r.error && (r.statusCode ?? 0) >= 200 && (r.statusCode ?? 0) < 400,
      status: r.statusCode ?? undefined,
      ms:     r.timing?.total,
      score:  scoreEngine(r).total,
      note:   scoreEngine(r).reason,
    }));
  }

  function buildProfileSummary(): PRProfileSummary {
    const j = profile.json;
    return {
      completeness:     profile.completeness,
      pageType:         'json',
      jsRenderRequired: false,
      paginationType:   profile.pagination?.type,
      paginationParam:  profile.pagination?.paramName,
      jsonListPath:     j?.listPath,
      jsonItemCount:    j?.count,
      jsonTitleKey:     j?.fieldGuess.title,
      jsonPriceKey:     j?.fieldGuess.price,
      jsonUrlKey:       j?.fieldGuess.url,
      jsonIdKey:        j?.fieldGuess.id,
    };
  }

  const generateM = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      await generateAndDownloadScraper(body);
      void recordStage({ stage: 'generate', sourceId: body.sourceId, displayName: body.displayName });
    },
    onError: (e: unknown) => {
      void recordStage({ stage: 'failed', error: { stage: 'generate', message: (e as Error).message ?? 'unknown' } });
    },
  });

  const openPRM = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      const result = await openOnboardingPR({
        ...body,
        feasibilityMatrix: buildFeasibilityMatrix(),
        profileSummary:    buildProfileSummary(),
        targetUrl:         url,
      });
      void recordStage({ stage: 'done', sourceId: body.sourceId, displayName: body.displayName, pr: result });
      return result;
    },
    onSuccess: () => {
      // See HTML path — same 4s grace before auto-clear.
      window.setTimeout(() => onPipelineComplete(), 4_000);
    },
    onError: (e: unknown) => {
      void recordStage({ stage: 'failed', error: { stage: 'pr', message: (e as Error).message ?? 'unknown' } });
    },
  });

  return (
    <div className="border-t border-slate-800 pt-3 space-y-3">
      <div className="flex items-center gap-2">
        <Package className="w-4 h-4 text-purple-300" aria-hidden />
        <h4 className="text-sm font-semibold">Stage 3 — Generate scraper</h4>
        <span className="text-[10px] px-1.5 py-px rounded border uppercase tracking-wide font-mono bg-purple-500/15 text-purple-200 border-purple-500/30">
          json
        </span>
      </div>
      <p className="text-xs text-slate-500">
        Renders a Python crawler + parser modeled on{' '}
        <code className="text-slate-300">backend/scrapers/cloudflare_workers/cex_uk/</code>. Slots into
        the existing <code className="text-slate-300">lib.crawler_base</code> framework — no browser engine.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field2 label="Scraper ID (registry key)">
          <input value={form.sourceId} onChange={(e) => update('sourceId', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false} placeholder="cex_uk" />
          <p className="text-[10px] text-slate-500 mt-0.5">snake_case, must match /^[a-z][a-z0-9_]+$/</p>
        </Field2>
        <Field2 label="Display name">
          <input value={form.displayName} onChange={(e) => update('displayName', e.target.value)}
            className="input w-full text-xs" placeholder="CeX UK" />
        </Field2>
        <Field2 label="URL pattern (use {term} + optional {page})">
          <input value={form.urlPattern} onChange={(e) => update('urlPattern', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false}
            placeholder="https://api.example.com/search?q={term}&page={page}" />
        </Field2>
        <Field2 label="Default max pages per term">
          <input type="number" min={1} max={10} value={form.defaultMaxPage}
            onChange={(e) => update('defaultMaxPage', Math.max(1, Number(e.target.value) || 1))}
            className="input w-full text-xs" />
        </Field2>
        <Field2 label="Products list path (dot-path; empty = root)">
          <input value={form.listPath} onChange={(e) => update('listPath', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false}
            placeholder="response.data" />
        </Field2>
        <Field2 label="Pagination">
          <div className="flex gap-1.5">
            <select value={form.paginationType}
              onChange={(e) => update('paginationType', e.target.value as JsonGenerateForm['paginationType'])}
              className="input text-xs">
              <option value="page">page</option>
              <option value="offset">offset</option>
              <option value="cursor">cursor</option>
            </select>
            <input value={form.paginationParam}
              onChange={(e) => update('paginationParam', e.target.value)}
              className="input flex-1 font-mono text-xs" placeholder="page" />
          </div>
        </Field2>
        <Field2 label="Title key">
          <input value={form.titleKey} onChange={(e) => update('titleKey', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false} placeholder="boxName" />
        </Field2>
        <Field2 label="Price key">
          <input value={form.priceKey} onChange={(e) => update('priceKey', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false} placeholder="sellPrice" />
        </Field2>
        <Field2 label="URL key">
          <input value={form.urlKey} onChange={(e) => update('urlKey', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false} placeholder="url" />
        </Field2>
        <Field2 label="ID key">
          <input value={form.idKey} onChange={(e) => update('idKey', e.target.value)}
            className="input w-full font-mono text-xs" spellCheck={false} placeholder="boxId" />
        </Field2>
      </div>
      <Field2 label={`Default search terms (canonical ${DEFAULT_SEARCH_TERMS_COUNT}-term list — prod overrides via env)`}>
        <textarea value={form.defaultSearchTerms} onChange={(e) => update('defaultSearchTerms', e.target.value)}
          rows={2} className="input w-full font-mono text-xs resize-y"
          placeholder="iphone 15, ps5, xbox series x" />
      </Field2>

      {generateM.error ? (
        <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
          {(generateM.error as Error).message}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2 flex-wrap">
        <button type="button" onClick={() => generateM.mutate()}
          disabled={generateM.isPending || openPRM.isPending}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded',
            'bg-purple-500/15 border border-purple-500/30 text-purple-200 hover:bg-purple-500/25',
            'disabled:opacity-50 disabled:cursor-not-allowed transition',
          )}>
          {generateM.isPending
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Generating…</>
            : <><Download className="w-3.5 h-3.5" aria-hidden /> Generate &amp; download zip</>}
        </button>
      </div>

      {generateM.isSuccess && (
        <div className="text-xs flex items-center gap-1.5 text-emerald-300">
          <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
          Zip downloaded. Unzip into <code className="text-slate-300">backend/scrapers/cloudflare_workers/</code> and follow the README.
        </div>
      )}

      {/* Stage 4 — Open PR (JSON branch) */}
      <div className="border-t border-slate-800 pt-3 space-y-3 mt-3">
        <div className="flex items-center gap-2">
          <GitPullRequest className="w-4 h-4 text-emerald-300" aria-hidden />
          <h4 className="text-sm font-semibold">Stage 4 — Open PR</h4>
        </div>
        <p className="text-xs text-slate-500">
          Pushes the same generated tree straight to{' '}
          <code className="text-slate-300">OTR-Technologies/otr.io</code> as a ready-to-review PR.
          Compose env-var snippet + reviewer TODOs are baked into the body.
        </p>

        {openPRM.error ? (
          <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
            {(openPRM.error as Error).message}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={() => openPRM.mutate()}
            disabled={generateM.isPending || openPRM.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded',
              'bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25',
              'disabled:opacity-50 disabled:cursor-not-allowed transition',
            )}>
            {openPRM.isPending
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Opening PR…</>
              : <><GitPullRequest className="w-3.5 h-3.5" aria-hidden /> Open PR on GitHub</>}
          </button>
        </div>

        {openPRM.isSuccess && openPRM.data && (
          <div className="border border-emerald-500/30 bg-emerald-500/10 rounded p-3 space-y-2">
            <div className="text-xs flex items-center gap-1.5 text-emerald-200 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
              PR opened — branch <code className="text-emerald-300">{openPRM.data.branch}</code>, commit <code className="text-emerald-300">{openPRM.data.commitSha.slice(0, 7)}</code>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <a href={openPRM.data.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-200 hover:text-emerald-100 underline">
                <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                View PR #{openPRM.data.number}
              </a>
              <button
                type="button"
                onClick={onPipelineComplete}
                title="Clear Stages 1–4 so you can onboard the next retailer immediately. Auto-clears after 4s either way."
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-200 hover:text-emerald-100 underline"
              >
                <Sparkles className="w-3.5 h-3.5" aria-hidden />
                Start new retailer
              </button>
            </div>
            <details className="text-[11px] text-slate-300">
              <summary className="cursor-pointer text-slate-400 hover:text-slate-200">{openPRM.data.files.length} files committed</summary>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 font-mono text-[10px]">
                {openPRM.data.files.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </details>
          </div>
        )}

        {openPRM.isSuccess && openPRM.data && (
          <Stage5Register
            // Keyed by (runId, PR#) so the local form state (poll
            // status, runsPerDay override) survives across re-renders
            // for the SAME run and is reset cleanly when a new run
            // takes its place rather than silently re-using the old
            // mutation handle.
            // runId from RunIdContext (provided at the OnboardTab
            // root) — see Stage5HistoryPanel rationale.
            pr={openPRM.data}
            sourceId={form.sourceId}
            displayName={form.displayName}
            pageType={profile.pageType}
            recommendedEngine={profile.recommendedEngine}
            recordStage={recordStage}
          />
        )}
      </div>
    </div>
  );
}

function GenerateSection({
  profile,
  url,
  results,
  recordStage,
  onPipelineComplete,
}: {
  profile:     SiteProfile;
  url:         string;
  results:     ProbeResult[];
  /** B5 — best-effort persistence callback (no-ops when no runId). */
  recordStage: (patch: Parameters<typeof patchOnboardRun>[1]) => Promise<void>;
  /** Called after a successful Stage-4 PR open — see top-level
   *  OnboardTab for what it clears. */
  onPipelineComplete: () => void;
}) {
  const isJson = profile.pageType === 'json';
  // For JSON profiles we still call all the hooks (rules-of-hooks)
  // but pass a stub profile to deriveInitialGenerateForm so it doesn't
  // crash on the missing listingCard. The JSX branch below renders
  // a placeholder instead of the form.
  const initial = useMemo(
    () => deriveInitialGenerateForm(isJson
      ? { ...profile, listingCard: { selector: '', tagName: '', count: 0, confidence: 0, reasons: [] } }
      : profile),
    [profile, isJson],
  );
  const [form, setForm] = useState<GenerateForm>(initial);
  const update = <K extends keyof GenerateForm>(k: K, v: GenerateForm[K]) => setForm({ ...form, [k]: v });

  /**
   * Single source of truth for the GenerateRequest body — both the
   * "Download zip" and "Open PR" mutations build their payload off
   * this. Throws on any pre-flight that the backend would also
   * reject so we surface the error before the network call.
   */
  function buildBody(): GenerateRequest {
    const terms = form.defaultSearchTerms
      .split(/[,;\n]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (terms.length === 0) throw new Error('At least one default search term is required');
    if (!form.urlPattern.includes('{term}')) {
      throw new Error('URL pattern must contain a {term} placeholder');
    }
    // listingSelector now comes from the form (pre-filled by Profile,
    // hand-editable by the operator). cf_http hard-requires it; the
    // docker_* engines tolerate a blank but the row would never parse
    // — keep the same precondition for both.
    if (!form.listingSelector.trim()) {
      throw new Error('Listing CSS selector is required — fill it in below before generating');
    }
    const body: GenerateRequest = {
      sourceId:           form.sourceId,
      displayName:        form.displayName,
      urlPattern:         form.urlPattern,
      // Kept for the docker_* template family which still consumes an
      // array of candidate selectors. The cf_http_html template ignores
      // this and reads `html.listingSelector` instead.
      listingSelectors:   [form.listingSelector.trim()],
      humanevasionTier:   profile.recommendedTier,
      defaultSearchTerms: terms,
      recommendedEngine:  form.engine,
      defaultMaxPage:     form.defaultMaxPage,
    };
    // Pagination is opt-in.
    //
    // Rule: only rewrite the URL + emit html.pagination* if the
    // operator (or auto-detect) has populated paginationParam. An
    // empty param means "no pagination" — the worker fetches one
    // page per term and the generated sources.js stays clean.
    //
    // For URLs with both `page=` and `offset=` (some retailers carry
    // both, e.g. `?page=1&offset=page*size`), pick whichever the
    // operator filled in. The worker only writes one param per
    // request; the other stays as the URL literal which the site
    // typically ignores or treats as a hint.
    const paginationConfigured = !!form.paginationParam.trim();
    if (paginationConfigured) {
      body.urlPattern = canonicaliseUrlPattern(body.urlPattern as string, {
        type:       form.paginationType,
        param:      form.paginationParam.trim(),
        base:       form.paginationBase,
        multiplier: form.paginationMultiplier,
      });
    }
    if (form.engine === 'cf_http') {
      // Mirror the backend `HtmlGenerateFields` shape. Empty inner
      // selectors are allowed — the generated parser skips that
      // column and the column lands as None at storage time.
      body.html = {
        listingSelector: form.listingSelector.trim(),
        titleSelector:   form.titleSelector.trim(),
        priceSelector:   form.priceSelector.trim(),
        urlSelector:     form.urlSelector.trim(),
        idSelector:      form.idSelector.trim(),
      };
      // Pagination fields are added conditionally so the generated
      // template doesn't bake in a misleading default (e.g. param=page,
      // type=page) for a single-page endpoint where the worker
      // shouldn't paginate at all.
      if (paginationConfigured) {
        body.html.paginationParam      = form.paginationParam.trim();
        body.html.paginationType       = form.paginationType;
        body.html.paginationBase       = form.paginationBase;
        body.html.paginationMultiplier = form.paginationMultiplier;
      }
    }
    return body;
  }

  /** Normalise the URL pattern so the generated sources.js entry
   *  matches what the worker will actually send on the wire.
   *  - page-style: substitute `{page}` for the existing page value
   *    (so reviewers see `&page={page}` in the template). The
   *    worker overrides via searchParams.set anyway, but the
   *    placeholder makes the intent visible.
   *  - offset / cursor: REMOVE the pagination param entirely from
   *    the template — leaving it confuses reviewers (literal
   *    `offset=0` while the worker actually writes `page * mult`).
   *  Always leaves `{term}` and any other params alone. */
  function canonicaliseUrlPattern(
    raw: string,
    pag: { type: 'page' | 'offset' | 'cursor'; param: string; base: number; multiplier: number },
  ): string {
    void pag.base; void pag.multiplier;
    if (!pag.param) return raw;
    let url: URL;
    try { url = new URL(raw); } catch { return raw; }
    // Find the actual key (case-preserving) so we don't double-write.
    let actual: string | null = null;
    for (const [k] of url.searchParams.entries()) {
      if (k.toLowerCase() === pag.param.toLowerCase()) { actual = k; break; }
    }
    if (pag.type === 'page') {
      if (actual) url.searchParams.set(actual, '{page}');
      // If the term key was already substituted to {term} via
      // inferUrlPattern, the toString below preserves it.
    } else {
      // offset / cursor — strip from the template; worker will write it.
      if (actual) url.searchParams.delete(actual);
    }
    let out = url.toString();
    out = out.replace(/%7B/g, '{').replace(/%7D/g, '}');
    return out;
  }

  /** Build the feasibility matrix the PR body renders from the
   *  Recon results we already have in scope — no extra round-trips. */
  function buildFeasibilityMatrix(): PRFeasibilityRow[] {
    return results.map((r) => ({
      engine:  r.engine,
      ok:      !r.error && (r.statusCode ?? 0) >= 200 && (r.statusCode ?? 0) < 400,
      status:  r.statusCode ?? undefined,
      ms:      r.timing?.total,
      score:   scoreEngine(r).total,
      note:    scoreEngine(r).reason,
    }));
  }

  /** Compact ProfileSummary for the PR body. Mirrors what the
   *  ProfileCard already shows. */
  function buildProfileSummary(): PRProfileSummary {
    return {
      completeness:         profile.completeness,
      listingSelector:      profile.listingCard?.selector,
      cookieBannerProvider: profile.cookieBanner?.provider,
      cookieAcceptSelector: profile.cookieBanner?.acceptSelector,
      jsRenderRequired:     profile.jsRenderNeed.required,
      paginationType:       profile.pagination?.type,
      paginationParam:      profile.pagination?.paramName,
    };
  }

  const generateM = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      await generateAndDownloadScraper(body);
      // B5 — persist Stage 3 progress. We don't get the file count
      // back from the download path so we omit `generated.files`;
      // the stage transition itself is the useful signal.
      void recordStage({
        stage:       'generate',
        sourceId:    body.sourceId,
        displayName: body.displayName,
      });
    },
    onError: (e: unknown) => {
      void recordStage({
        stage: 'failed',
        error: { stage: 'generate', message: (e as Error).message ?? 'unknown' },
      });
    },
  });

  const openPRM = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      const result = await openOnboardingPR({
        ...body,
        feasibilityMatrix: buildFeasibilityMatrix(),
        profileSummary:    buildProfileSummary(),
        targetUrl:         url,
      });
      // B5 — terminal stage. PATCH with stage='done' so the history
      // row hits the green-tick state immediately, plus the PR
      // payload so the row carries a clickable PR link.
      void recordStage({
        stage:       'done',
        sourceId:    body.sourceId,
        displayName: body.displayName,
        pr:          result,
      });
      return result;
    },
    onSuccess: () => {
      // Auto-clear after 4 seconds so the operator can read the PR
      // link / file list before the form resets. They can also click
      // "Start new retailer" sooner. Manual reset via the button is
      // available too — the timer is just for the impatient path.
      window.setTimeout(() => onPipelineComplete(), 4_000);
    },
    onError: (e: unknown) => {
      void recordStage({
        stage: 'failed',
        error: { stage: 'pr', message: (e as Error).message ?? 'unknown' },
      });
    },
  });

  if (isJson) {
    return <JsonGenerateSection profile={profile} url={url} results={results} recordStage={recordStage} onPipelineComplete={onPipelineComplete} />;
  }

  return (
    <div className="border-t border-slate-800 pt-3 space-y-3">
      <div className="flex items-center gap-2">
        <Package className="w-4 h-4 text-purple-300" aria-hidden />
        <h4 className="text-sm font-semibold">Stage 3 — Generate scraper</h4>
      </div>
      <p className="text-xs text-slate-500">
        Renders the discovered config into a complete scraper directory
        and downloads it as a zip. Stage 4 below opens a PR with the
        same files. The output layout depends on the engine you chose:
        {' '}
        {form.engine === 'cf_http' ? (
          <>
            <code className="text-slate-300">backend/scrapers/cloudflare_workers/&lt;source_id&gt;/</code>
            {' '}— Python crawler + parser, runs inside the existing
            {' '}<code>cf_workers_scrapers</code> container.
          </>
        ) : (
          <>
            <code className="text-slate-300">backend/scrapers/dockerized_browsers/real_browsers/&lt;source_id&gt;/</code>
            {' '}— Go source + Dockerfile + go.mod + compose snippet.
          </>
        )}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field2 label="Scraper ID (registry key)">
          <input
            value={form.sourceId}
            placeholder="argos_uk_docker"
            onChange={(e) => update('sourceId', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
          />
          <p className="text-[10px] text-slate-500 mt-0.5">
            snake_case, must match /^[a-z][a-z0-9_]+$/.
            {form.engine === 'cf_http'
              ? <> For <code>cf_http</code>, the bare site id (no suffix) is conventional, e.g. <code>ebuyer_uk</code>.</>
              : <> Convention: end with <code>_docker</code> for the Docker engine variant.</>}
          </p>
        </Field2>
        <Field2 label="Display name">
          <input
            value={form.displayName}
            placeholder="Argos UK (docker)"
            onChange={(e) => update('displayName', e.target.value)}
            className="input w-full text-xs"
          />
        </Field2>
        <Field2 label="URL pattern (use {term} + {page})">
          <input
            value={form.urlPattern}
            placeholder="https://www.example.com/search?q={term}&page={page}"
            onChange={(e) => update('urlPattern', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
          />
        </Field2>
        <Field2 label="Default max pages per term">
          <input
            type="number"
            min={1}
            max={10}
            value={form.defaultMaxPage}
            onChange={(e) => update('defaultMaxPage', Math.max(1, Number(e.target.value) || 1))}
            className="input w-full text-xs"
          />
        </Field2>
      </div>
      <Field2 label={`Default search terms (canonical ${DEFAULT_SEARCH_TERMS_COUNT}-term list — prod overrides via env)`}>
        <textarea
          value={form.defaultSearchTerms}
          onChange={(e) => update('defaultSearchTerms', e.target.value)}
          rows={2}
          className="input w-full font-mono text-xs resize-y"
          placeholder="iphone 15, ps5, xbox series x"
        />
      </Field2>

      {/* Engine + selector overrides.
          The three engine choices map to the three template families
          the generator dispatcher supports. The selector inputs are
          required for cf_http; the docker_* engines ignore the inner
          fields and use only `listingSelector`. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-slate-800 pt-3">
        <Field2 label="Engine (overrides Recon's pick)">
          <select
            value={form.engine}
            onChange={(e) => update('engine', e.target.value as GenerateForm['engine'])}
            className="input w-full text-xs"
          >
            <option value="cf_http">cf_http — HTML via Cloudflare worker</option>
            <option value="docker_browser">docker_browser — headless Chrome</option>
            <option value="docker_http">docker_http — direct HTTP (Go)</option>
          </select>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Recon recommended <code>{profile.recommendedEngine || '—'}</code>.
            Override only if you have a reason.
          </p>
        </Field2>
        <Field2 label="Listing CSS selector (required)">
          <input
            value={form.listingSelector}
            placeholder=".s-productthumbbox / li.product-card / [data-testid='product-grid-item']"
            onChange={(e) => update('listingSelector', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
          />
        </Field2>
        <Field2 label="Title selector (relative to card)">
          <input
            value={form.titleSelector}
            placeholder=".productdescriptionname / h3"
            onChange={(e) => update('titleSelector', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
            disabled={form.engine !== 'cf_http'}
          />
        </Field2>
        <Field2 label="Price selector (relative to card)">
          <input
            value={form.priceSelector}
            placeholder=".curprice / .price"
            onChange={(e) => update('priceSelector', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
            disabled={form.engine !== 'cf_http'}
          />
        </Field2>
        <Field2 label="URL selector (relative to card)">
          <input
            value={form.urlSelector}
            placeholder="a.product-link / a"
            onChange={(e) => update('urlSelector', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
            disabled={form.engine !== 'cf_http'}
          />
        </Field2>
        <Field2 label="ID selector or attribute (optional)">
          <input
            value={form.idSelector}
            placeholder="[data-product-id] / .sku"
            onChange={(e) => update('idSelector', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
            disabled={form.engine !== 'cf_http'}
          />
        </Field2>
      </div>
      <p className="text-[10px] text-slate-500 -mt-1">
        Inner selectors only apply to the <code>cf_http</code> engine.
        The <code>docker_*</code> engines parse via the generated Go
        code and only need the listing-card selector.
      </p>

      {/* Pagination configuration — opt-in.
          Auto-detected from the URL when there's a recognisable
          param (offset / start / from; page / _pgn; cursor / token).
          Leave the param empty to disable pagination entirely — the
          worker just fetches one page per term. The operator fills
          in offset-multiplier (e.g. `size=48` → 48) only when the
          site genuinely uses offset-by-N pagination. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 border-t border-slate-800 pt-3">
        <Field2 label="Pagination type">
          <select
            value={form.paginationType}
            onChange={(e) => update('paginationType', e.target.value as GenerateForm['paginationType'])}
            className="input w-full text-xs"
            disabled={!form.paginationParam.trim()}
          >
            <option value="page">page — ?page=N+base</option>
            <option value="offset">offset — ?offset=N×size</option>
            <option value="cursor">cursor — token-based</option>
          </select>
        </Field2>
        <Field2 label="Pagination param (leave blank to disable)">
          <input
            value={form.paginationParam}
            placeholder="(none — single page per term)"
            onChange={(e) => update('paginationParam', e.target.value)}
            className="input w-full font-mono text-xs"
            spellCheck={false}
          />
        </Field2>
        <Field2 label="Base (first index)">
          <input
            type="number"
            value={form.paginationBase}
            onChange={(e) => update('paginationBase', Number(e.target.value) || 0)}
            className="input w-full text-xs"
            disabled={!form.paginationParam.trim()}
          />
          <p className="text-[10px] text-slate-500 mt-0.5">
            {form.paginationType === 'page'
              ? <>Page 1 → base 1. Page 0 → base 0.</>
              : <>Usually 0 for offset / cursor.</>}
          </p>
        </Field2>
        <Field2 label={form.paginationType === 'offset' ? 'Offset size (multiplier)' : 'Multiplier'}>
          <input
            type="number"
            value={form.paginationMultiplier}
            min={1}
            onChange={(e) => update('paginationMultiplier', Math.max(1, Number(e.target.value) || 1))}
            className="input w-full text-xs"
            disabled={!form.paginationParam.trim() || form.paginationType !== 'offset'}
          />
          <p className="text-[10px] text-slate-500 mt-0.5">
            {form.paginationType === 'offset'
              ? <>Items per page from the site (e.g. <code>size=48</code> → 48). Worker sends <code>offset = pageIdx × {form.paginationMultiplier}</code>.</>
              : <>Only applies to offset-style pagination.</>}
          </p>
        </Field2>
      </div>
      {!form.paginationParam.trim() && (
        <p className="text-[10px] text-amber-300/80 -mt-1">
          Pagination is disabled — the worker will fetch a single page
          per term. Fill in <code>Pagination param</code> above to enable.
        </p>
      )}

      {generateM.error ? (
        <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
          {(generateM.error as Error).message}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => generateM.mutate()}
          disabled={generateM.isPending || openPRM.isPending}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded',
            'bg-purple-500/15 border border-purple-500/30 text-purple-200 hover:bg-purple-500/25',
            'disabled:opacity-50 disabled:cursor-not-allowed transition',
          )}
        >
          {generateM.isPending
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Generating…</>
            : <><Download className="w-3.5 h-3.5" aria-hidden /> Generate &amp; download zip</>}
        </button>
      </div>

      {generateM.isSuccess && (
        <div className="text-xs flex items-center gap-1.5 text-emerald-300">
          <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
          Zip downloaded. Unzip into{' '}
          <code className="text-slate-300">
            {form.engine === 'cf_http'
              ? 'backend/scrapers/cloudflare_workers/'
              : 'backend/scrapers/dockerized_browsers/real_browsers/'}
          </code>{' '}
          and follow the README.
        </div>
      )}

      {/* ── Stage 4 — Open PR ─────────────────────────────────────────
           Logically a separate stage (auto-PR on the monorepo), but
           shares all the form state with Stage 3 so it lives in the
           same component. The visible header + divider make Stage 4
           obvious in the UI without a separate hooks/state split. */}
      <div className="border-t border-slate-800 pt-3 space-y-3 mt-3">
        <div className="flex items-center gap-2">
          <GitPullRequest className="w-4 h-4 text-emerald-300" aria-hidden />
          <h4 className="text-sm font-semibold">Stage 4 — Open PR</h4>
        </div>
        <p className="text-xs text-slate-500">
          Skip the zip download — push the same generated tree straight to{' '}
          <code className="text-slate-300">OTR-Technologies/otr.io</code> as a
          ready-to-review PR with the feasibility matrix, profile evidence,
          compose snippet, and cleaner-stub TODO baked into the body.
        </p>

        {openPRM.error ? (
          <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
            {(openPRM.error as Error).message}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => openPRM.mutate()}
            disabled={generateM.isPending || openPRM.isPending}
            title="Open a real GitHub PR on the configured monorepo with this scraper"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded',
              'bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25',
              'disabled:opacity-50 disabled:cursor-not-allowed transition',
            )}
          >
            {openPRM.isPending
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Opening PR…</>
              : <><GitPullRequest className="w-3.5 h-3.5" aria-hidden /> Open PR on GitHub</>}
          </button>
        </div>

        {openPRM.isSuccess && openPRM.data && (
          <div className="border border-emerald-500/30 bg-emerald-500/10 rounded p-3 space-y-2">
            <div className="text-xs flex items-center gap-1.5 text-emerald-200 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
              PR opened — branch <code className="text-emerald-300">{openPRM.data.branch}</code>, commit <code className="text-emerald-300">{openPRM.data.commitSha.slice(0, 7)}</code>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href={openPRM.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-200 hover:text-emerald-100 underline"
              >
                <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                View PR #{openPRM.data.number}
              </a>
              <button
                type="button"
                onClick={onPipelineComplete}
                title="Clear all Stages 1–4 so you can onboard the next retailer immediately. Auto-clears after 4s either way."
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-200 hover:text-emerald-100 underline"
              >
                <Sparkles className="w-3.5 h-3.5" aria-hidden />
                Start new retailer
              </button>
            </div>
            <details className="text-[11px] text-slate-300">
              <summary className="cursor-pointer text-slate-400 hover:text-slate-200">{openPRM.data.files.length} files committed</summary>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 font-mono text-[10px]">
                {openPRM.data.files.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </details>
          </div>
        )}

        {openPRM.isSuccess && openPRM.data && (
          <Stage5Register
            pr={openPRM.data}
            sourceId={form.sourceId}
            displayName={form.displayName}
            pageType={profile.pageType}
            recommendedEngine={profile.recommendedEngine}
            recordStage={recordStage}
          />
        )}
      </div>
    </div>
  );
}

function Field2({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-[11px] text-slate-400">{label}</label>
      {children}
    </div>
  );
}

function ProfileCard({ profile, onSave, saved }: { profile: SiteProfile; onSave: () => void; saved: boolean }) {
  const completenessClass = profile.completeness >= 80
    ? 'text-emerald-300'
    : profile.completeness >= 60
      ? 'text-amber-300'
      : 'text-red-300';

  if (profile.pageType === 'json') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>Completeness:</span>
          <span className={cn('font-medium', completenessClass)}>{profile.completeness}/100</span>
          {profile.completeness < 60 && (
            <span className="text-[11px] text-amber-300">manual review recommended</span>
          )}
        </div>
        <JsonProfileBlock profile={profile} onSave={onSave} saved={saved} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span>Completeness:</span>
        <span className={cn('font-medium', completenessClass)}>{profile.completeness}/100</span>
        {profile.completeness < 60 && (
          <span className="text-[11px] text-amber-300">manual review recommended</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <ProfileField
          icon={<FileText className="w-3.5 h-3.5" aria-hidden />}
          label="Listing-card selector"
          value={profile.listingCard
            ? <code className="text-emerald-200">{profile.listingCard.selector}</code>
            : <span className="text-slate-500">not auto-detected</span>}
          detail={profile.listingCard
            ? `${profile.listingCard.count} ${profile.listingCard.tagName}s · confidence ${profile.listingCard.confidence}/100 · ${profile.listingCard.reasons.slice(0, 2).join(', ')}`
            : 'fill in manually before code generation'}
        />

        <ProfileField
          icon={<Cookie className="w-3.5 h-3.5" aria-hidden />}
          label="Cookie banner"
          value={profile.cookieBanner
            ? <span className="text-emerald-200">{profile.cookieBanner.provider}</span>
            : <span className="text-slate-500">none detected</span>}
          detail={profile.cookieBanner
            ? `accept: ${profile.cookieBanner.acceptSelector} · confidence ${profile.cookieBanner.confidence}`
            : 'humanevasion has a generic-cookie fallback that catches most missed banners at runtime'}
        />

        <ProfileField
          icon={<Sparkles className="w-3.5 h-3.5" aria-hidden />}
          label="JS rendering required?"
          value={profile.jsRenderNeed.required
            ? <span className="text-amber-200">yes — use a browser engine</span>
            : <span className="text-emerald-200">no — HTTP engine is enough</span>}
          detail={profile.jsRenderNeed.reasons.join('; ')}
        />

        <ProfileField
          icon={<ChevronRight className="w-3.5 h-3.5" aria-hidden />}
          label="Pagination"
          value={profile.pagination
            ? <span className="text-emerald-200">{profile.pagination.type}{profile.pagination.paramName ? `: ${profile.pagination.paramName}` : ''}</span>
            : <span className="text-slate-500">not auto-detected</span>}
          detail={profile.pagination
            ? `confidence ${profile.pagination.confidence}${profile.pagination.selector ? ` · ${profile.pagination.selector}` : ''}`
            : 'manual review — listing pages can use cursor / infinite-scroll / button-click that didn\'t match heuristics'}
        />

        <ProfileField
          icon={<Beaker className="w-3.5 h-3.5" aria-hidden />}
          label="humanevasion tier"
          value={<code className="text-emerald-200">{profile.recommendedTier}</code>}
          detail={
            profile.recommendedTier === 'vm'
              ? 'heaviest — block signals or known anti-bot suggest VM-tier input synthesis'
              : profile.recommendedTier === 'cdp'
                ? 'safe default — works for most targets with cookie banners or modest evasion needs'
                : 'lightest — JS-only event dispatch, fine for low-friction targets'
          }
        />

        <ProfileField
          icon={<CheckCircle2 className="w-3.5 h-3.5" aria-hidden />}
          label="Recommended engine"
          value={<code className="text-emerald-200">{profile.recommendedEngine}</code>}
          detail="picked from the Recon scoring above"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        {saved && (
          <span className="text-[11px] text-emerald-300 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" aria-hidden /> saved
          </span>
        )}
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 transition"
        >
          <Save className="w-3.5 h-3.5" aria-hidden /> Save profile
        </button>
      </div>
    </div>
  );
}

/** JSON-profile renderer. Mirrors the shape of the HTML ProfileCard
 *  fields so the page layout doesn't shift when switching between
 *  page types. Shows the resolved list path, item count, sample item,
 *  and the auto-guessed field mappings (operator overrides them in
 *  Stage 3). */
function JsonProfileBlock({ profile, onSave, saved }: { profile: SiteProfile; onSave: () => void; saved: boolean }) {
  const json = profile.json;
  if (!json) {
    return (
      <div className="border border-amber-500/30 bg-amber-500/5 text-amber-200 text-xs rounded p-3">
        Couldn't resolve a products array in the JSON response. Either the body preview was too truncated
        (4KB cap) or the path didn't match anything. Re-run with an explicit "Products list path" in Stage 1.
      </div>
    );
  }
  const guess = json.fieldGuess;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <ProfileField
          icon={<FileText className="w-3.5 h-3.5" aria-hidden />}
          label="Products list path"
          value={<code className="text-emerald-200">{json.listPath || '$ (root)'}</code>}
          detail={`${json.count} items · confidence ${json.confidence}/100 · ${json.reasons.slice(0, 2).join(', ')}`}
        />
        <ProfileField
          icon={<Sparkles className="w-3.5 h-3.5" aria-hidden />}
          label="Field guesses (override in Stage 3)"
          value={
            <div className="space-y-0.5 text-[11px] font-mono">
              <div>title: <span className={guess.title ? 'text-emerald-200' : 'text-slate-500'}>{guess.title ?? '—'}</span></div>
              <div>price: <span className={guess.price ? 'text-emerald-200' : 'text-slate-500'}>{guess.price ?? '—'}</span></div>
              <div>image: <span className={guess.image ? 'text-emerald-200' : 'text-slate-500'}>{guess.image ?? '—'}</span></div>
              <div>url:&nbsp;&nbsp;<span className={guess.url ? 'text-emerald-200' : 'text-slate-500'}>{guess.url ?? '—'}</span></div>
              <div>id:&nbsp;&nbsp;&nbsp;<span className={guess.id ? 'text-emerald-200' : 'text-slate-500'}>{guess.id ?? '—'}</span></div>
            </div>
          }
          detail={`picked from ${json.itemKeys.length} observed keys`}
        />
        <ProfileField
          icon={<ChevronRight className="w-3.5 h-3.5" aria-hidden />}
          label="Pagination"
          value={profile.pagination
            ? <span className="text-emerald-200">{profile.pagination.type}{profile.pagination.paramName ? `: ${profile.pagination.paramName}` : ''}</span>
            : <span className="text-slate-500">none in URL</span>}
          detail={profile.pagination
            ? `confidence ${profile.pagination.confidence}`
            : 'response may carry a cursor — check sample item below for next-page-token-style keys'}
        />
        <ProfileField
          icon={<CheckCircle2 className="w-3.5 h-3.5" aria-hidden />}
          label="Recommended engine"
          value={<code className="text-emerald-200">{profile.recommendedEngine}</code>}
          detail="picked from the Recon scoring above — JSON APIs typically work on direct_http"
        />
      </div>

      <details className="border border-slate-800 rounded bg-slate-900/30">
        <summary className="cursor-pointer text-[11px] text-slate-400 hover:text-slate-200 px-2.5 py-1.5">
          Sample item ({json.itemKeys.length} keys)
        </summary>
        <pre className="text-[10px] font-mono text-slate-300 px-2.5 pb-2.5 overflow-x-auto">{json.sampleItem}</pre>
      </details>

      <div className="flex items-center justify-end gap-2 pt-1">
        {saved && (
          <span className="text-[11px] text-emerald-300 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" aria-hidden /> saved
          </span>
        )}
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 transition"
        >
          <Save className="w-3.5 h-3.5" aria-hidden /> Save profile
        </button>
      </div>
    </div>
  );
}

function ProfileField({ icon, label, value, detail }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; detail: string;
}) {
  return (
    <div className="border border-slate-800 bg-slate-900/30 rounded p-2.5 space-y-1">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xs">{value}</div>
      <p className="text-[11px] text-slate-500 leading-tight">{detail}</p>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function humanBytes(n: number): string {
  if (n < 1024)        return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}

